import React, { useState } from 'react';
import { 
  Sword, 
  Trophy, 
  Flame, 
  Star, 
  Crown, 
  Clock, 
  Calendar,
  Target,
  Timer,
  Users,
  Eye,
  Bolt,
  Shield,
  Building2,
  Lock,
  Briefcase,
  MapPin,
  Zap,
  Heart,
  Activity,
  Coffee,
  Brain,
  Droplets,
  Moon,
  Scale,
  TrendingUp,
  TrendingDown,
  Award,
  Gift,
  BadgeCheck,
  UserPlus,
  Code,
  CheckCircle,
  X,
  Sparkles,
  DollarSign,
  Flag
} from 'lucide-react';

const EnterpriseGamificationHub = ({ user, userInfo, showNotification }) => {
  const [activeTab, setActiveTab] = useState('battle-zone');
  const [selectedBattle, setSelectedBattle] = useState(null);


  // Helper function to parse battle titles and extract challenger/defender info
  const parseBattleTitle = (title) => {
    // Check if title is valid
    if (!title || typeof title !== 'string') {
      console.warn('Invalid title passed to parseBattleTitle:', title);
      return null;
    }

    // Extract parts from format like "New York 🇺🇸 vs London 🇬🇧"
    const parts = title.split(' vs ');
    if (parts.length !== 2) {
      console.warn('Title does not contain " vs " separator:', title);
      return null;
    }
    
    const parseLocation = (locationStr) => {
      // Check if locationStr is valid
      if (!locationStr || typeof locationStr !== 'string') {
        console.warn('Invalid location string:', locationStr);
        return { name: 'Unknown', flag: '🏳️' };
      }

      try {
        const flagRegex = /[\u{1F1E6}-\u{1F1FF}][\u{1F1E6}-\u{1F1FF}]/u;
        const flagMatch = locationStr.match(flagRegex);
        const flag = flagMatch ? flagMatch[0] : '🏳️';
        const name = locationStr.replace(flagRegex, '').trim();
        return { name, flag };
      } catch (error) {
        console.error('Error parsing location:', locationStr, error);
        return { name: locationStr.toString(), flag: '🏳️' };
      }
    };
    
    const challenger = parseLocation(parts[0]);
    const defender = parseLocation(parts[1]);
    
    return { challenger, defender };
  };

  // Elite Battle Zone Data with Enhanced Gamification
  const battleZoneChallenges = [
    {
      id: 1,
      name: "35-Day Health Sprint Championship",
      frequency: "BIANNUAL",
      duration: "35 days", 
      urgency: "LEGENDARY",
      difficulty: "Legendary",
      timeLeft: "12 days",
      description: "The most prestigious wellness combat challenge. Total body optimization across all health metrics with real-time performance tracking.",
      exclusiveNote: "🏆 Only 2 opportunities per year - March & September registration windows",
      participants: 8924,
      maxParticipants: 10000,
      rewards: { first: 100000, second: 75000, third: 50000, top10: 10000 },
      badges: ["Health Deity", "Sprint Legend", "35-Day Conqueror"],
      multiplier: "10x points for first 3 days",
      location: "Global Elite Arena",
      status: "REGISTRATION OPEN"
    },
    {
      id: 2, 
      name: "65-Day Transformation Conquest",
      frequency: "ANNUAL",
      duration: "65 days",
      urgency: "ULTRA", 
      difficulty: "Master",
      timeLeft: "89 days",
      description: "Ultimate endurance challenge for complete health metamorphosis. Advanced biometric tracking and AI-powered coaching included.",
      exclusiveNote: "🔥 Single annual opportunity - Summer exclusive window only",
      participants: 6432,
      maxParticipants: 8000,
      rewards: { first: 100000, second: 75000, third: 50000, top10: 10000 },
      badges: ["Transformation Master", "65-Day Titan", "Evolution Champion"],
      multiplier: "Progressive weekly multipliers",
      location: "Platinum Transformation Lab",
      status: "REGISTRATION OPEN"
    },
    {
      id: 3,
      name: "95-Day Legend Quest Ultimate",
      frequency: "ANNUAL", 
      duration: "95 days",
      urgency: "LEGENDARY",
      difficulty: "Legendary",
      timeLeft: "156 days",
      description: "The apex of wellness competition. Only for the elite 1%. Comprehensive health overhaul with dedicated coaching team.",
      exclusiveNote: "👑 Most exclusive challenge - October launch, limited to top performers",
      participants: 2847,
      maxParticipants: 5000,
      rewards: { first: 100000, second: 75000, third: 50000, top10: 10000 },
      badges: ["Wellness Deity", "Legend Immortal", "95-Day God"],
      multiplier: "Epic milestone bonuses",
      location: "Legendary Champions Colosseum",
      status: "WAITLIST ONLY"
    },
    {
      id: 4,
      name: "24-Hour Power Surge Battle",
      frequency: "WEEKLY",
      duration: "24 hours",
      urgency: "IMMEDIATE",
      difficulty: "Intense",
      timeLeft: "14 hours",
      description: "Non-stop 24-hour wellness marathon! Sleep tracking, step goals, heart rate zones, and hydration challenges.",
      exclusiveNote: "⚡ WEEKLY FRENZY: Fast-paced competition with hourly bonus multipliers",
      participants: 12459,
      maxParticipants: 15000,
      rewards: { first: 5000, second: 2500, third: 1500, top10: 500 },
      badges: ["Sprint Master", "24-Hour Warrior", "Speed Champion"],
      multiplier: "Hourly bonus multipliers",
      location: "Global Sprint Arena",
      status: "REGISTRATION OPEN"
    }
  ];

  // Corporate Wellness Competition Data
  const corporateCompetitions = [
    {
      id: 1,
      name: "Microsoft vs Google Health Championships",
      type: "MEGA_CORPORATE",
      status: "LIVE",
      participants: { microsoft: 89245, google: 76842 },
      duration: "90 days",
      timeLeft: "42 days",
      categories: ["Steps", "Sleep", "Heart Rate", "Meditation", "Hydration"],
      currentLeader: "Microsoft",
      prizes: {
        winning_company: "$2.5M wellness budget boost",
        employees: "Premium gym memberships + $5k wellness stipend",
        insurance: "25% health insurance discount for all employees"
      },
      requirements: "Gold membership ($60/year) + company verification",
      companyCode: "REQUIRED",
      verificationLevel: "Enterprise Grade"
    },
    {
      id: 2,
      name: "Finance Titans Wellness War",
      type: "SECTOR_BATTLE",
      status: "REGISTRATION",
      companies: ["JPMorgan Chase", "Goldman Sachs", "Morgan Stanley", "Bank of America"],
      participants: 145680,
      duration: "60 days", 
      timeLeft: "8 days to register",
      categories: ["Stress Management", "Physical Activity", "Nutrition", "Sleep Quality"],
      prizes: {
        winning_company: "$1.8M employee wellness fund",
        top_department: "$50k team rewards",
        individual_winners: "Luxury fitness retreats + $3k bonus"
      },
      requirements: "Gold membership + Financial sector verification",
      companyCode: "FIN2024",
      verificationLevel: "Sector Authenticated"
    },
    {
      id: 3,
      name: "Healthcare Heroes Challenge",
      type: "INDUSTRY_FOCUS",
      status: "UPCOMING",
      description: "Hospital systems & healthcare companies compete in wellness excellence",
      expectedParticipants: 200000,
      duration: "120 days",
      timeLeft: "Starting in 15 days",
      specialFeatures: ["24/7 health monitoring", "Peer support networks", "Mental wellness focus"],
      prizes: {
        winning_organization: "$3M research & wellness equipment fund",
        departments: "Advanced health monitoring devices",
        employees: "Comprehensive wellness packages + insurance premium reductions"
      },
      requirements: "Gold membership + Healthcare sector authentication",
      companyCode: "HEALTH2024",
      verificationLevel: "Medical Grade"
    }
  ];

  // Department Competition Data
  const departmentCompetitions = [
    {
      id: 1,
      company: "Microsoft",
      departments: [
        { name: "Engineering", participants: 12450, score: 94250, rank: 1 },
        { name: "Sales", participants: 8960, score: 89420, rank: 2 },
        { name: "Marketing", participants: 5680, score: 78910, rank: 3 },
        { name: "Operations", participants: 6340, score: 72150, rank: 4 }
      ],
      prize: "$25k department wellness budget",
      timeLeft: "18 days",
      categories: ["Team steps", "Group challenges", "Wellness participation"]
    },
    {
      id: 2,
      company: "Google",
      departments: [
        { name: "Product", participants: 11200, score: 91850, rank: 1 },
        { name: "Engineering", participants: 15600, score: 88920, rank: 2 },
        { name: "Business", participants: 7800, score: 76540, rank: 3 },
        { name: "Research", participants: 4500, score: 69880, rank: 4 }
      ],
      prize: "$25k department wellness budget", 
      timeLeft: "18 days",
      categories: ["Innovation wellness", "Team collaboration", "Health innovation"]
    }
  ];

  // Corporate Reward Tiers
  const corporateRewardTiers = [
    {
      level: "Bronze Corporate",
      requirement: "500+ employees participating",
      benefits: [
        "5% insurance premium reduction",
        "Basic gym membership discounts", 
        "Quarterly wellness reports",
        "Employee wellness dashboard"
      ],
      cost: "Gold membership required for all participants"
    },
    {
      level: "Silver Corporate", 
      requirement: "1000+ employees participating",
      benefits: [
        "10% insurance premium reduction",
        "Premium gym partnerships",
        "Monthly wellness coaching",
        "Advanced health analytics",
        "Stress management programs"
      ],
      cost: "Gold membership + $10k corporate fee"
    },
    {
      level: "Gold Corporate",
      requirement: "2000+ employees participating", 
      benefits: [
        "20% insurance premium reduction",
        "Executive wellness retreats",
        "On-site wellness facilities",
        "Personal health coaching",
        "Mental health support programs",
        "$50k annual wellness budget"
      ],
      cost: "Gold membership + $25k corporate fee"
    },
    {
      level: "Platinum Corporate",
      requirement: "5000+ employees participating",
      benefits: [
        "30% insurance premium reduction", 
        "Custom wellness programs",
        "Executive health screenings",
        "Workplace wellness infrastructure",
        "Dedicated wellness team",
        "$150k annual wellness budget",
        "Priority healthcare access"
      ],
      cost: "Gold membership + $75k corporate fee"
    }
  ];

  // Enhanced Geographical Battles with Virtual NFT Prize System - 7 Day Battles
  const geographicalBattles = [
    {
      id: 1,
      title: "New York 🇺🇸 vs London 🇬🇧",
      description: "Atlantic Powerhouses",
      participants: 45672,
      timeLeft: "5d 14h",
      intensity: 10,
      battles: 1247,
      categories: ['Cardio', 'Sleep'],
      prizes: { 
        first: { type: "Mythic NFT", name: "🏆 Empire Crown", rarity: "Mythic" },
        second: { type: "Legendary Title", name: "🗽 Liberty Guardian", rarity: "Legendary" },
        third: { type: "Epic Card", name: "🎩 Royal Bridge", rarity: "Epic" }
      }
    },
    {
      id: 2,
      title: "Tokyo 🇯🇵 vs Seoul 🇰🇷",
      description: "East Asian Titans",
      participants: 38492,
      timeLeft: "6d 8h",
      intensity: 9,
      battles: 943,
      categories: ['Mental', 'Activity'],
      prizes: { 
        first: { type: "Legendary NFT", name: "🌅 Rising Sun Champion", rarity: "Legendary" },
        second: { type: "Epic Title", name: "🥈 Neon Warrior", rarity: "Epic" },
        third: { type: "Rare Card", name: "🥉 Tech Samurai", rarity: "Rare" }
      }
    },
    {
      id: 3,
      title: "Beijing 🇨🇳 vs Shanghai 🇨🇳",
      description: "Chinese Megacities",
      participants: 89432,
      timeLeft: "6d 16h",
      intensity: 10,
      battles: 2847,
      categories: ['Nutrition', 'Activity'],
      prizes: { 
        first: { type: "Mythic NFT", name: "🐉 Dragon Emperor", rarity: "Mythic" },
        second: { type: "Legendary Title", name: "🏮 Pearl Guardian", rarity: "Legendary" },
        third: { type: "Epic Card", name: "🏯 Great Wall Defender", rarity: "Epic" }
      }
    },
    {
      id: 4,
      title: "Moscow 🇷🇺 vs St. Petersburg 🇷🇺",
      description: "Russian Capital Rivalry",
      participants: 67891,
      timeLeft: "1d 7h",
      intensity: 9,
      battles: 1923,
      categories: ['Cardio', 'Recovery'],
      prizes: { 
        first: { type: "Legendary NFT", name: "❄️ Ice Tsar", rarity: "Legendary" },
        second: { type: "Epic Title", name: "🏰 Kremlin Knight", rarity: "Epic" },
        third: { type: "Rare Card", name: "⭐ Red Star Warrior", rarity: "Rare" }
      }
    },
    {
      id: 5,
      title: "Paris 🇫🇷 vs Lyon 🇫🇷",
      description: "French Excellence",
      participants: 43567,
      timeLeft: "5d 12h",
      intensity: 8,
      battles: 1234,
      categories: ['Nutrition', 'Mental'],
      prizes: { 
        first: { type: "Epic NFT", name: "🗼 Eiffel Master", rarity: "Epic" },
        second: { type: "Rare Title", name: "🥐 Gourmet Champion", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "🍷 Renaissance Artist", rarity: "Uncommon" }
      }
    },
    {
      id: 6,
      title: "Sydney 🇦🇺 vs Melbourne 🇦🇺",
      description: "Australian Coastal War",
      participants: 38745,
      timeLeft: "4d 9h",
      intensity: 8,
      battles: 1087,
      categories: ['Activity', 'Cardio'],
      prizes: { 
        first: { type: "Epic NFT", name: "🦘 Outback Legend", rarity: "Epic" },
        second: { type: "Rare Title", name: "🏄 Surf Champion", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "🐨 Coastal Guardian", rarity: "Uncommon" }
      }
    },
    {
      id: 7,
      title: "Mumbai 🇮🇳 vs Karachi 🇵🇰",
      description: "South Asian Showdown",
      participants: 78234,
      timeLeft: "3d 14h",
      intensity: 9,
      battles: 2145,
      categories: ['Mental', 'Nutrition'],
      prizes: { 
        first: { type: "Legendary NFT", name: "🗡️ Monsoon King", rarity: "Legendary" },
        second: { type: "Epic Title", name: "🏺 Spice Master", rarity: "Epic" },
        third: { type: "Rare Card", name: "🦚 Peacock Warrior", rarity: "Rare" }
      }
    },
    {
      id: 8,
      title: "Tehran 🇮🇷 vs Riyadh 🇸🇦",
      description: "Middle Eastern Championship",
      participants: 52678,
      timeLeft: "2d 3h",
      intensity: 8,
      battles: 1456,
      categories: ['Recovery', 'Mental'],
      prizes: { 
        first: { type: "Epic NFT", name: "🏜️ Desert Sultan", rarity: "Epic" },
        second: { type: "Rare Title", name: "🕌 Palace Guardian", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "💫 Arabian Star", rarity: "Uncommon" }
      }
    },
    {
      id: 9,
      title: "Istanbul 🇹🇷 vs Kuwait City 🇰🇼",
      description: "Gulf-Mediterranean",
      participants: 34892,
      timeLeft: "6d 18h",
      intensity: 7,
      battles: 892,
      categories: ['Sleep', 'Nutrition'],
      prizes: { 
        first: { type: "Rare NFT", name: "🌉 Bridge Master", rarity: "Rare" },
        second: { type: "Uncommon Title", name: "🏛️ Byzantine Hero", rarity: "Uncommon" },
        third: { type: "Common Card", name: "⛵ Trade Captain", rarity: "Common" }
      }
    },
    {
      id: 10,
      title: "Lagos 🇳🇬 vs Cairo 🇪🇬",
      description: "African Powerhouse",
      participants: 61345,
      timeLeft: "1d 21h",
      intensity: 9,
      battles: 1789,
      categories: ['Activity', 'Cardio'],
      prizes: { 
        first: { type: "Legendary NFT", name: "🦁 Sahara King", rarity: "Legendary" },
        second: { type: "Epic Title", name: "🏺 Pharaoh's Heir", rarity: "Epic" },
        third: { type: "Rare Card", name: "🌍 Nile Guardian", rarity: "Rare" }
      }
    },
    {
      id: 11,
      title: "Johannesburg 🇿🇦 vs Cape Town 🇿🇦",
      description: "South African Rivalry",
      participants: 29567,
      timeLeft: "5d 12h",
      intensity: 8,
      battles: 743,
      categories: ['Mental', 'Recovery'],
      prizes: { 
        first: { type: "Epic NFT", name: "💎 Diamond Crown", rarity: "Epic" },
        second: { type: "Rare Title", name: "🦓 Safari Champion", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "🏔️ Table Mountain Hero", rarity: "Uncommon" }
      }
    },
    {
      id: 12,
      title: "Berlin 🇩🇪 vs Amsterdam 🇳🇱",
      description: "European Innovation",
      participants: 35298,
      timeLeft: "4d 22h",
      intensity: 8,
      battles: 943,
      categories: ['Sleep', 'Mental'],
      prizes: { 
        first: { type: "Epic NFT", name: "⚡ Innovation Master", rarity: "Epic" },
        second: { type: "Rare Title", name: "🌷 Canal Conqueror", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "🏛️ Euro Architect", rarity: "Uncommon" }
      }
    },
    {
      id: 13,
      title: "São Paulo 🇧🇷 vs Buenos Aires 🇦🇷",
      description: "South American Championship",
      participants: 56123,
      timeLeft: "3d 11h",
      intensity: 9,
      battles: 1834,
      categories: ['Activity', 'Cardio'],
      prizes: { 
        first: { type: "Legendary NFT", name: "🏆 Carnival King", rarity: "Legendary" },
        second: { type: "Epic Title", name: "⚽ Tango Master", rarity: "Epic" },
        third: { type: "Rare Card", name: "🌴 Tropical Warrior", rarity: "Rare" }
      }
    },
    {
      id: 14,
      title: "Dubai 🇦🇪 vs Singapore 🇸🇬",
      description: "Global Hub Showdown",
      participants: 29847,
      timeLeft: "2d 5h",
      intensity: 7,
      battles: 721,
      categories: ['Mental', 'Sleep'],
      prizes: { 
        first: { type: "Epic NFT", name: "💎 Desert Jewel", rarity: "Epic" },
        second: { type: "Rare Title", name: "🏝️ Island Guardian", rarity: "Rare" },
        third: { type: "Uncommon Card", name: "🌆 Skyline Hero", rarity: "Uncommon" }
      }
    }
  ];

  // Challenge Creation System
  const availableChallenges = {
    countries: [
      { name: "United States", flag: "🇺🇸", members: 2847000, canChallenge: ["United Kingdom", "Canada", "Japan"] },
      { name: "United Kingdom", flag: "🇬🇧", members: 1940000, canChallenge: ["United States", "Germany", "France"] },
      { name: "Japan", flag: "🇯🇵", members: 1680000, canChallenge: ["South Korea", "United States", "China"] },
      { name: "Germany", flag: "🇩🇪", members: 1520000, canChallenge: ["United Kingdom", "France", "Netherlands"] }
    ],
    cities: [
      { name: "New York City", flag: "🇺🇸", members: 125680, canChallenge: ["Los Angeles", "London", "Toronto"] },
      { name: "London", flag: "🇬🇧", members: 98720, canChallenge: ["New York City", "Paris", "Berlin"] },
      { name: "Tokyo", flag: "🇯🇵", members: 89240, canChallenge: ["Seoul", "Singapore", "Hong Kong"] },
      { name: "Los Angeles", flag: "🇺🇸", members: 87960, canChallenge: ["New York City", "San Francisco", "Miami"] }
    ],
    states: [
      { name: "California", flag: "🇺🇸", members: 890000, canChallenge: ["Texas", "New York", "Florida"] },
      { name: "Texas", flag: "🇺🇸", members: 756000, canChallenge: ["California", "Florida", "New York"] },
      { name: "New York", flag: "🇺🇸", members: 620000, canChallenge: ["California", "Texas", "Illinois"] }
    ],
    provinces: [
      { name: "Ontario", flag: "🇨🇦", members: 345000, canChallenge: ["Quebec", "British Columbia", "Alberta"] },
      { name: "Quebec", flag: "🇨🇦", members: 289000, canChallenge: ["Ontario", "British Columbia", "Nova Scotia"] },
      { name: "New South Wales", flag: "🇦🇺", members: 198000, canChallenge: ["Ontario", "Quebec", "Victoria"] }
    ]
  };

  // Global Rankings Data
  const globalRankings = {
    countries: [
      { rank: 1, name: "United States", flag: "🇺🇸", members: 2847000, powerScore: 94580, cities: 156 },
      { rank: 2, name: "United Kingdom", flag: "🇬🇧", members: 1940000, powerScore: 91240, cities: 89 },
      { rank: 3, name: "Japan", flag: "🇯🇵", members: 1680000, powerScore: 89760, cities: 67 },
      { rank: 4, name: "Germany", flag: "🇩🇪", members: 1520000, powerScore: 87420, cities: 78 },
      { rank: 5, name: "South Korea", flag: "🇰🇷", members: 1340000, powerScore: 85680, cities: 45 }
    ],
    topCities: [
      { rank: 1, name: "New York City", country: "🇺🇸", members: 125680, powerScore: 96850, region: "North America" },
      { rank: 2, name: "London", country: "🇬🇧", members: 98720, powerScore: 94250, region: "Europe" },
      { rank: 3, name: "Tokyo", country: "🇯🇵", members: 89240, powerScore: 92180, region: "Asia" },
      { rank: 4, name: "Los Angeles", country: "🇺🇸", members: 87960, powerScore: 90840, region: "North America" },
      { rank: 5, name: "Seoul", country: "🇰🇷", members: 76890, powerScore: 89420, region: "Asia" }
    ]
  };

  // Regional Power Structure
  const regionalPowers = [
    {
      region: "North America",
      totalMembers: 3240000,
      powerScore: 95680,
      topCountries: ["🇺🇸 United States", "🇨🇦 Canada", "🇲🇽 Mexico"],
      dominantCity: "New York City",
      specialization: "Urban Fitness & Technology Integration"
    },
    {
      region: "Europe",
      totalMembers: 2890000,
      powerScore: 93420,
      topCountries: ["🇬🇧 United Kingdom", "🇩🇪 Germany", "🇫🇷 France"],
      dominantCity: "London",
      specialization: "Cycling & Sustainable Wellness"
    },
    {
      region: "Asia",
      totalMembers: 2670000,
      powerScore: 91840,
      topCountries: ["🇯🇵 Japan", "🇰🇷 South Korea", "🇸🇬 Singapore"],
      dominantCity: "Tokyo",
      specialization: "Sleep Optimization & Mental Wellness"
    }
  ];

  const tabs = [
    { 
      id: 'battle-zone', 
      name: 'Battle Zone', 
      icon: Sword, 
      color: 'from-red-500 to-orange-500',
      description: 'Ultra-competitive elite challenges'
    },
    { 
      id: 'teams-guilds', 
      name: 'Teams & Guilds', 
      icon: Shield, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Team competitions & guild battles'
    },
    { 
      id: 'corporate', 
      name: 'Corporate', 
      icon: Building2, 
      color: 'from-green-500 to-emerald-500',
      description: 'Company vs company competitions'
    },
    { 
      id: 'competition-central', 
      name: 'Competition Central', 
      icon: Crown, 
      color: 'from-purple-500 to-pink-500',
      description: 'Elite championship features'
    }
  ];

  const renderBattleZone = () => (
    <div className="space-y-8">
      {/* Elegant Professional Header */}
      <div className="relative p-8 overflow-hidden shadow-2xl bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 rounded-2xl">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-indigo-600/5 to-purple-600/5"></div>
        <div className="absolute top-0 right-0 w-64 h-64 translate-x-32 -translate-y-32 rounded-full bg-gradient-to-bl from-amber-400/3 to-transparent"></div>
        
        <div className="relative z-10 space-y-6 text-center">
          {/* Professional Title Section */}
          <div className="flex items-center justify-center space-x-4">
            <div className="p-3 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <Sword className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="mb-2 text-4xl font-bold text-white">
                Elite Battle Zone
              </h1>
              <p className="text-lg font-medium text-blue-200">Premium Wellness Championships</p>
            </div>
          </div>
          
          {/* Elegant Status Badges */}
          <div className="flex justify-center space-x-4">
            <div className="px-4 py-2 border rounded-lg bg-white/10 backdrop-blur-md border-white/20">
              <span className="text-sm font-semibold text-emerald-300">🏆 Elite Access</span>
            </div>
            <div className="px-4 py-2 border rounded-lg bg-white/10 backdrop-blur-md border-white/20">
              <span className="text-sm font-semibold text-amber-300">⚡ Live Events</span>
            </div>
            <div className="px-4 py-2 border rounded-lg bg-white/10 backdrop-blur-md border-white/20">
              <span className="text-sm font-semibold text-purple-300">👑 Premium Tier</span>
            </div>
          </div>

          {/* Professional Stats Dashboard */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 border bg-white/10 backdrop-blur-md border-emerald-400/20 rounded-xl">
              <div className="text-2xl font-bold text-emerald-400">2,847</div>
              <div className="text-xs font-medium text-emerald-300">Active Participants</div>
            </div>
            <div className="p-4 border bg-white/10 backdrop-blur-md border-blue-400/20 rounded-xl">
              <div className="text-2xl font-bold text-blue-400">195</div>
              <div className="text-xs font-medium text-blue-300">Countries Participating</div>
            </div>
            <div className="p-4 border bg-white/10 backdrop-blur-md border-amber-400/20 rounded-xl">
              <div className="text-2xl font-bold text-amber-400">89%</div>
              <div className="text-xs font-medium text-amber-300">Success Rate</div>
            </div>
            <div className="p-4 border bg-white/10 backdrop-blur-md border-purple-400/20 rounded-xl">
              <div className="text-2xl font-bold text-purple-400">2.1M</div>
              <div className="text-xs font-medium text-purple-300">MediPoints Pool</div>
            </div>
          </div>
        </div>
      </div>

      {/* Beautiful Challenge Cards Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {battleZoneChallenges.filter(challenge => challenge.frequency !== 'WEEKLY').map((challenge, index) => {
          const registrationProgress = Math.round((challenge.participants / challenge.maxParticipants) * 100);
          const isOpen = challenge.status === 'REGISTRATION OPEN';
          
          return (
            <div key={challenge.id} className="relative overflow-hidden transition-all duration-500 bg-white border border-gray-100 shadow-lg group rounded-2xl hover:shadow-xl">
              
              {/* Elegant Header with Gradient */}
              <div className={`p-6 ${
                challenge.urgency === 'LEGENDARY' ? 'bg-gradient-to-r from-purple-600 to-indigo-700' :
                challenge.urgency === 'ULTRA' ? 'bg-gradient-to-r from-blue-600 to-cyan-700' :
                'bg-gradient-to-r from-emerald-600 to-teal-700'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold leading-tight text-white">
                      {challenge.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 text-xs font-semibold text-white rounded-full bg-white/20">
                        {challenge.frequency}
                      </span>
                      <span className="px-2 py-1 text-xs font-semibold text-white rounded-full bg-white/20">
                        {challenge.difficulty}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-white/80">Time Left</div>
                    <div className="text-lg font-bold text-white">{challenge.timeLeft}</div>
                  </div>
                </div>
              </div>

              {/* Professional Content Section */}
              <div className="p-6">
                {/* Description */}
                <p className="mb-6 text-sm leading-relaxed text-gray-600">
                  {challenge.description}
                </p>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-3 text-center rounded-lg bg-gray-50">
                    <Users className="w-5 h-5 mx-auto mb-2 text-blue-600" />
                    <div className="text-sm font-semibold text-gray-700">Participants</div>
                    <div className="text-lg font-bold text-gray-900">{challenge.participants.toLocaleString()}</div>
                  </div>
                  <div className="p-3 text-center rounded-lg bg-gray-50">
                    <Trophy className="w-5 h-5 mx-auto mb-2 text-amber-600" />
                    <div className="text-sm font-semibold text-gray-700">1st Prize</div>
                    <div className="text-lg font-bold text-gray-900">{(challenge.rewards.first / 1000)}K MP</div>
                  </div>
                </div>

                {/* Elegant Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between mb-2 text-sm font-medium text-gray-700">
                    <span>Registration Progress</span>
                    <span>{registrationProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div 
                      className={`h-2 rounded-full transition-all duration-1000 ${
                        registrationProgress > 90 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                        registrationProgress > 75 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                        'bg-gradient-to-r from-emerald-500 to-teal-500'
                      }`}
                      style={{ width: `${registrationProgress}%` }}
                    ></div>
                  </div>
                  {registrationProgress > 90 && (
                    <div className="mt-1 text-xs font-semibold text-red-600 animate-pulse">
                      ⚠️ Nearly Full - Limited Spots Remaining
                    </div>
                  )}
                </div>

                {/* Exclusive Note */}
                <div className="p-3 mb-6 border-l-4 border-blue-500 rounded-r-lg bg-blue-50">
                  <p className="text-sm font-medium text-blue-800">
                    {challenge.exclusiveNote}
                  </p>
                </div>

                {/* Professional Action Button */}
                <button className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${
                  isOpen ? (
                    challenge.urgency === 'LEGENDARY' ? 
                      'bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800' :
                    challenge.urgency === 'ULTRA' ?
                      'bg-gradient-to-r from-blue-600 to-cyan-700 hover:from-blue-700 hover:to-cyan-800' :
                      'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800'
                  ) : 'bg-gray-400 cursor-not-allowed'
                }`}
                  disabled={!isOpen}
                  onClick={() => setSelectedBattle(challenge)}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {isOpen ? (
                      <>
                        <Sword className="w-4 h-4" />
                        <span>Join Challenge</span>
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4" />
                        <span>Join Waitlist</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Professional Call-to-Action */}
      <div className="p-8 text-center bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl">
        <h3 className="mb-4 text-2xl font-bold text-gray-900">
          Ready to Join Elite Competition?
        </h3>
        <p className="max-w-2xl mx-auto mb-6 text-gray-600">
          Challenge yourself in our premium wellness competitions. Compete against the best, 
          earn exclusive rewards, and transform your health journey.
        </p>
        <div className="flex justify-center space-x-4">
          <button className="px-6 py-3 font-semibold text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700">
            View All Challenges
          </button>
          <button className="px-6 py-3 font-semibold text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50">
            Learn More
          </button>
        </div>
      </div>

      {/* 🚀 COMING SOON IN FUTURE PHASES - Battle Zone */}
      <div className="relative p-8 overflow-hidden border-2 border-dashed rounded-3xl border-orange-300/60 bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-50/80">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/5 via-amber-400/5 to-yellow-400/5 animate-pulse"></div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 shadow-xl bg-gradient-to-r from-orange-500 to-amber-600 rounded-2xl animate-bounce">
              <Zap className="w-8 h-8 text-white" />
            </div>
          </div>
          <h3 className="mb-3 text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600">
            🚀 Coming Soon in Future Phases
          </h3>
          <p className="max-w-3xl mx-auto mb-4 text-lg font-semibold text-gray-700">
            Advanced AI-powered matchmaking, real-time biometric integration, global tournament brackets, 
            elite coaching systems, and revolutionary wellness battle mechanics!
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-sm font-bold text-orange-700">
            <span className="px-3 py-1 rounded-full bg-orange-100/80">🏆 Championship Leagues</span>
            <span className="px-3 py-1 rounded-full bg-amber-100/80">🤖 AI Battle Coach</span>
            <span className="px-3 py-1 rounded-full bg-yellow-100/80">⚡ Live Biometrics</span>
            <span className="px-3 py-1 rounded-full bg-orange-100/80">🌍 Global Rankings</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCorporate = () => (
    <div className="space-y-8">
      {/* 🏢 CORPORATE WELLNESS COMMAND CENTER HEADER - ENHANCED */}
      <div className="relative p-10 overflow-hidden shadow-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-3xl">
        {/* Complex Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/15 via-purple-600/10 to-indigo-600/15 backdrop-blur-sm"></div>
        <div className="absolute top-0 right-0 translate-x-32 -translate-y-32 rounded-full w-96 h-96 bg-gradient-to-bl from-cyan-400/12 to-purple-400/8 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 -translate-x-40 translate-y-40 rounded-full w-80 h-80 bg-gradient-to-tr from-blue-400/12 to-indigo-400/8 animate-pulse"></div>
        <div className="absolute w-64 h-64 transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 bg-gradient-to-r from-emerald-400/5 to-cyan-400/5 animate-spin" style={{animationDuration: '20s'}}></div>
        
        {/* Floating Corporate Icons */}
        <div className="absolute p-2 top-8 left-8 bg-white/10 backdrop-blur-sm rounded-xl animate-bounce" style={{animationDelay: '0s', animationDuration: '3s'}}>
          <Briefcase className="w-6 h-6 text-blue-300" />
        </div>
        <div className="absolute p-2 top-16 right-16 bg-white/10 backdrop-blur-sm rounded-xl animate-bounce" style={{animationDelay: '1s', animationDuration: '3s'}}>
          <Trophy className="w-6 h-6 text-amber-300" />
        </div>
        <div className="absolute p-2 bottom-8 left-16 bg-white/10 backdrop-blur-sm rounded-xl animate-bounce" style={{animationDelay: '2s', animationDuration: '3s'}}>
          <Crown className="w-6 h-6 text-purple-300" />
        </div>
        <div className="absolute p-2 bottom-16 right-8 bg-white/10 backdrop-blur-sm rounded-xl animate-bounce" style={{animationDelay: '1.5s', animationDuration: '3s'}}>
          <Award className="w-6 h-6 text-emerald-300" />
        </div>
        
        <div className="relative z-10 space-y-8 text-center">
          <div className="flex items-center justify-center space-x-6">
            <div className="relative group">
              <div className="absolute transition duration-300 opacity-75 -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 rounded-3xl blur-sm group-hover:opacity-100 animate-pulse"></div>
              <div className="relative p-5 transition-all duration-500 transform shadow-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl hover:scale-110 hover:rotate-3">
                <Building2 className="text-white w-14 h-14" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-6xl font-black text-transparent bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text animate-pulse">
                CORPORATE WELLNESS
              </h1>
              <h2 className="text-3xl font-bold text-transparent bg-gradient-to-r from-blue-300 via-purple-300 to-emerald-400 bg-clip-text">
                ⚡ ELITE COMMAND CENTER ⚡
              </h2>
              <div className="flex items-center justify-center space-x-2 text-lg font-semibold text-amber-300">
                <Sparkles className="w-5 h-5 animate-spin" />
                <span>Enterprise-Grade Gamification Platform</span>
                <Sparkles className="w-5 h-5 animate-spin" />
              </div>
            </div>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <p className="mb-4 text-xl font-medium leading-relaxed text-blue-100">
              🏢 <span className="text-2xl font-black text-white bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text">ULTRA-PROFESSIONAL ENTERPRISE ECOSYSTEM</span>
            </p>
            <p className="text-lg font-medium text-blue-200">
              Create secure company challenges • Department warfare • Inter-company competitions • Automatic code generation • 
              Real-time ROI tracking • <span className="font-bold text-emerald-300">$18.2M+ proven healthcare savings</span> across our elite network.
            </p>
          </div>
          
          <div className="grid max-w-4xl grid-cols-2 gap-4 mx-auto text-sm md:grid-cols-4">
            <div className="relative group">
              <div className="absolute transition duration-300 -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative flex items-center px-4 py-3 space-x-3 transition-all duration-300 transform bg-white/20 backdrop-blur-md rounded-xl hover:bg-white/30 hover:scale-105">
                <Lock className="w-5 h-5 text-amber-300 animate-pulse" />
                <span className="font-bold text-white">🔐 Military-Grade Security</span>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute transition duration-300 -inset-1 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative flex items-center px-4 py-3 space-x-3 transition-all duration-300 transform bg-white/20 backdrop-blur-md rounded-xl hover:bg-white/30 hover:scale-105">
                <Zap className="w-5 h-5 text-emerald-300 animate-pulse" />
                <span className="font-bold text-white">⚡ Auto Code Generation</span>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute transition duration-300 -inset-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative flex items-center px-4 py-3 space-x-3 transition-all duration-300 transform bg-white/20 backdrop-blur-md rounded-xl hover:bg-white/30 hover:scale-105">
                <Crown className="w-5 h-5 text-purple-300 animate-pulse" />
                <span className="font-bold text-white">👑 Gold Elite Access</span>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute transition duration-300 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative flex items-center px-4 py-3 space-x-3 transition-all duration-300 transform bg-white/20 backdrop-blur-md rounded-xl hover:bg-white/30 hover:scale-105">
                <TrendingUp className="w-5 h-5 text-cyan-300 animate-pulse" />
                <span className="font-bold text-white">📈 Real-Time ROI</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 📊 ENTERPRISE-GRADE ROI STATISTICS - GAMIFIED */}
      <div className="relative p-10 overflow-hidden bg-white shadow-2xl rounded-3xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50"></div>
        <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32 rounded-full bg-gradient-to-bl from-blue-100/50 to-transparent"></div>
        
        <div className="relative z-10">
          <div className="mb-10 text-center">
            <div className="flex items-center justify-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl animate-pulse">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-4xl font-black text-transparent bg-gradient-to-r from-gray-800 via-blue-600 to-emerald-600 bg-clip-text">
                💰 PROVEN ROI & ENTERPRISE DOMINANCE
              </h3>
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl animate-pulse">
                <Crown className="w-8 h-8 text-white" />
              </div>
            </div>
            <p className="text-lg font-bold text-gray-600">
              🚀 Real battlefield results from our 1,247+ corporate wellness warriors worldwide
            </p>
            <div className="flex items-center justify-center mt-2 space-x-2 text-sm font-semibold text-emerald-600">
              <Sparkles className="w-4 h-4 animate-spin" />
              <span>Live data updated every 60 seconds</span>
              <Sparkles className="w-4 h-4 animate-spin" />
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {/* Total Company Savings */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border shadow-lg border-emerald-200 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-emerald-600 to-green-700 rounded-xl animate-bounce">
                    <DollarSign className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-black text-emerald-700 animate-pulse">$18.2M+</div>
                <div className="mb-2 text-sm font-bold text-emerald-600">Total Company Savings</div>
                <div className="px-2 py-1 text-xs font-semibold rounded-full text-emerald-500 bg-emerald-100">
                  ⬆️ +$2.3M this month
                </div>
              </div>
            </div>

            {/* Insurance Claims Drop */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border border-red-200 shadow-lg bg-gradient-to-br from-red-50 via-rose-50 to-red-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-red-600 to-rose-700 rounded-xl animate-bounce" style={{animationDelay: '0.2s'}}>
                    <TrendingDown className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-black text-red-700 animate-pulse" style={{animationDelay: '0.2s'}}>-31%</div>
                <div className="mb-2 text-sm font-bold text-red-600">Insurance Claims Drop</div>
                <div className="px-2 py-1 text-xs font-semibold text-red-500 bg-red-100 rounded-full">
                  🎯 Best in industry
                </div>
              </div>
            </div>

            {/* Productivity Boost */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border border-blue-200 shadow-lg bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-blue-600 to-cyan-700 rounded-xl animate-bounce" style={{animationDelay: '0.4s'}}>
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-black text-blue-700 animate-pulse" style={{animationDelay: '0.4s'}}>+42%</div>
                <div className="mb-2 text-sm font-bold text-blue-600">Productivity Boost</div>
                <div className="px-2 py-1 text-xs font-semibold text-blue-500 bg-blue-100 rounded-full">
                  ⚡ Peak performance
                </div>
              </div>
            </div>

            {/* Return on Investment */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border border-purple-200 shadow-lg bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl animate-bounce" style={{animationDelay: '0.6s'}}>
                    <Sparkles className="w-8 h-8 text-white animate-spin" />
                  </div>
                </div>
                <div className="text-3xl font-black text-purple-700 animate-pulse" style={{animationDelay: '0.6s'}}>6.3x</div>
                <div className="mb-2 text-sm font-bold text-purple-600">Return on Investment</div>
                <div className="px-2 py-1 text-xs font-semibold text-purple-500 bg-purple-100 rounded-full">
                  🔥 Explosive ROI
                </div>
              </div>
            </div>

            {/* Average Annual Savings */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border shadow-lg bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl border-amber-200">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-amber-600 to-yellow-700 rounded-xl animate-bounce" style={{animationDelay: '0.8s'}}>
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-black text-amber-700 animate-pulse" style={{animationDelay: '0.8s'}}>$2,400+</div>
                <div className="mb-2 text-sm font-bold text-amber-600">Avg Annual Savings</div>
                <div className="px-2 py-1 text-xs font-semibold rounded-full text-amber-500 bg-amber-100">
                  💰 Per employee
                </div>
              </div>
            </div>

            {/* Client Satisfaction */}
            <div className="relative group">
              <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
              <div className="relative p-6 text-center transition-all duration-500 transform border border-indigo-200 shadow-lg bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 rounded-2xl hover:scale-110 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-gradient-to-r from-indigo-600 to-blue-700 rounded-xl animate-bounce" style={{animationDelay: '1s'}}>
                    <BadgeCheck className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-black text-indigo-700 animate-pulse" style={{animationDelay: '1s'}}>94%</div>
                <div className="mb-2 text-sm font-bold text-indigo-600">Client Satisfaction</div>
                <div className="px-2 py-1 text-xs font-semibold text-indigo-500 bg-indigo-100 rounded-full">
                  👑 Elite tier rating
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Achievement Banner */}
          <div className="p-4 mt-8 border bg-gradient-to-r from-emerald-100 via-blue-100 to-purple-100 rounded-2xl border-emerald-200">
            <div className="flex items-center justify-center space-x-6 text-sm font-bold">
              <div className="flex items-center space-x-2 text-emerald-700">
                <Trophy className="w-5 h-5 animate-pulse" />
                <span>Industry Leader</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-700">
                <Award className="w-5 h-5 animate-pulse" />
                <span>Fortune 500 Trusted</span>
              </div>
              <div className="flex items-center space-x-2 text-purple-700">
                <Star className="w-5 h-5 animate-pulse" />
                <span>5-Star Platform</span>
              </div>
              <div className="flex items-center space-x-2 text-amber-700">
                <Flame className="w-5 h-5 animate-pulse" />
                <span>Market Domination</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🏆 COMPREHENSIVE WELLNESS COMPETITION CATEGORIES - ENHANCED */}
      <div className="space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-6 space-x-4">
            <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl animate-pulse">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-4xl font-black text-transparent bg-gradient-to-r from-gray-800 via-green-600 to-blue-600 bg-clip-text">
              🏆 COMPREHENSIVE WELLNESS BATTLEGROUNDS
            </h3>
            <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl animate-pulse">
              <Flame className="w-8 h-8 text-white" />
            </div>
          </div>
          <p className="mb-2 text-lg font-bold text-gray-600">Six ultra-competitive gamified wellness categories designed for maximum engagement</p>
          <div className="flex items-center justify-center space-x-2 text-sm font-semibold text-emerald-600">
            <Zap className="w-4 h-4 animate-pulse" />
            <span>Real-time tracking • Instant leaderboards • Automatic secure code generation</span>
            <Zap className="w-4 h-4 animate-pulse" />
          </div>
        </div>
        
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {/* STEP COMPETITIONS */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 border-blue-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">STEP WARS</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-blue-600">
                    <Sword className="w-4 h-4" />
                    <span>Department vs Department Combat</span>
                  </p>
                </div>
              </div>
              
              {/* Enhanced Stats */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-blue-600">342</div>
                  </div>
                  <div className="p-3 border rounded-lg bg-cyan-50 border-cyan-200">
                    <div className="text-sm font-semibold text-gray-600">Warriors</div>
                    <div className="text-2xl font-black text-cyan-600">28,476</div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Weekly Target Progress</span>
                    <span className="text-blue-600">87%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse" style={{width: '87%'}}></div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-blue-800">Battle Features</span>
                    <Target className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-xs font-semibold text-blue-700">5K+ daily steps • 30/60 day campaigns • Live leaderboards • Team bonuses • Achievement badges</p>
                </div>
                
                {/* CTA Button */}
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 hover:scale-105 hover:shadow-xl">
                  ⚔️ JOIN STEP WARFARE
                </button>
              </div>
            </div>
          </div>

          {/* HYDRATION WARS */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 shadow-lg border-cyan-500 rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl">
                    <Droplets className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">HYDRATION WARS</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-cyan-600">
                    <Droplets className="w-4 h-4" />
                    <span>Smart Water Domination</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg bg-cyan-50 border-cyan-200">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-cyan-600">267</div>
                  </div>
                  <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="text-sm font-semibold text-gray-600">Hydration Heroes</div>
                    <div className="text-2xl font-black text-blue-600">19,834</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Daily Goal Achievement</span>
                    <span className="text-cyan-600">92%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 animate-pulse" style={{width: '92%'}}></div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg bg-cyan-50 border-cyan-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-cyan-800">Hydration Arsenal</span>
                    <Droplets className="w-4 h-4 text-cyan-600 animate-bounce" />
                  </div>
                  <p className="text-xs font-semibold text-cyan-700">Daily targets • Team challenges • Smart bottle integration • Hydration streaks • Bonus multipliers</p>
                </div>
                
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 hover:scale-105 hover:shadow-xl">
                  💧 DOMINATE HYDRATION
                </button>
              </div>
            </div>
          </div>

          {/* SLEEP OPTIMIZATION */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 border-indigo-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
                    <Moon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">SLEEP MASTERY</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-indigo-600">
                    <Moon className="w-4 h-4" />
                    <span>Elite Recovery Excellence</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-indigo-200 rounded-lg bg-indigo-50">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-indigo-600">198</div>
                  </div>
                  <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                    <div className="text-sm font-semibold text-gray-600">Sleep Warriors</div>
                    <div className="text-2xl font-black text-purple-600">15,623</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Optimal Sleep Achievement</span>
                    <span className="text-indigo-600">89%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse" style={{width: '89%'}}></div>
                  </div>
                </div>
                
                <div className="p-4 border border-indigo-200 rounded-lg bg-indigo-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-indigo-800">Sleep Tech Stack</span>
                    <Moon className="w-4 h-4 text-indigo-600 animate-pulse" />
                  </div>
                  <p className="text-xs font-semibold text-indigo-700">Quality scoring • REM tracking • Team averages • Sleep coaching • Recovery analytics</p>
                </div>
                
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 hover:scale-105 hover:shadow-xl">
                  🌙 MASTER RECOVERY
                </button>
              </div>
            </div>
          </div>

          {/* MINDFULNESS MASTERS */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 border-purple-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">MINDFUL MASTERY</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-purple-600">
                    <Brain className="w-4 h-4" />
                    <span>Mental Wellness Command</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-purple-600">156</div>
                  </div>
                  <div className="p-3 border border-pink-200 rounded-lg bg-pink-50">
                    <div className="text-sm font-semibold text-gray-600">Zen Masters</div>
                    <div className="text-2xl font-black text-pink-600">11,247</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Mindfulness Streaks</span>
                    <span className="text-purple-600">76%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" style={{width: '76%'}}></div>
                  </div>
                </div>
                
                <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-purple-800">Mindful Arsenal</span>
                    <Brain className="w-4 h-4 text-purple-600 animate-pulse" />
                  </div>
                  <p className="text-xs font-semibold text-purple-700">Guided sessions • Stress tracking • Mindful challenges • Meditation streaks • Zen bonuses</p>
                </div>
                
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 hover:scale-105 hover:shadow-xl">
                  🧘 ACHIEVE ZEN MASTERY
                </button>
              </div>
            </div>
          </div>

          {/* NUTRITION WARRIORS */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 border-green-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                    <Coffee className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">NUTRITION WARS</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-green-600">
                    <Coffee className="w-4 h-4" />
                    <span>Healthy Eating Domination</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-green-200 rounded-lg bg-green-50">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-green-600">289</div>
                  </div>
                  <div className="p-3 border rounded-lg bg-emerald-50 border-emerald-200">
                    <div className="text-sm font-semibold text-gray-600">Nutrition Elite</div>
                    <div className="text-2xl font-black text-emerald-600">22,156</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Healthy Choice Rate</span>
                    <span className="text-green-600">84%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 animate-pulse" style={{width: '84%'}}></div>
                  </div>
                </div>
                
                <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-green-800">Nutrition Command</span>
                    <Coffee className="w-4 h-4 text-green-600 animate-pulse" />
                  </div>
                  <p className="text-xs font-semibold text-green-700">Low sugar goals • Meal tracking • Nutrition coaching • Healthy streaks • Macro optimization</p>
                </div>
                
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-105 hover:shadow-xl">
                  🥗 CONQUER NUTRITION
                </button>
              </div>
            </div>
          </div>

          {/* TRANSFORMATION CHALLENGE */}
          <div className="relative group">
            <div className="absolute transition duration-500 opacity-50 -inset-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-t-4 border-orange-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-4">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl blur-sm"></div>
                  <div className="relative p-4 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl">
                    <Scale className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-2xl font-black text-gray-800">TRANSFORMATION</h4>
                  <p className="flex items-center space-x-1 text-sm font-bold text-orange-600">
                    <Scale className="w-4 h-4" />
                    <span>Ultimate Body Optimization</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-orange-200 rounded-lg bg-orange-50">
                    <div className="text-sm font-semibold text-gray-600">Active Companies</div>
                    <div className="text-2xl font-black text-orange-600">234</div>
                  </div>
                  <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                    <div className="text-sm font-semibold text-gray-600">Transform Elite</div>
                    <div className="text-2xl font-black text-red-600">18,927</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">Goal Achievement</span>
                    <span className="text-orange-600">91%</span>
                  </div>
                  <div className="w-full h-3 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-orange-500 to-red-500 animate-pulse" style={{width: '91%'}}></div>
                  </div>
                </div>
                
                <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-orange-800">Transform Arsenal</span>
                    <Scale className="w-4 h-4 text-orange-600 animate-pulse" />
                  </div>
                  <p className="text-xs font-semibold text-orange-700">Progress tracking • Body composition • Coaching support • Milestone rewards • Transform streaks</p>
                </div>
                
                <button className="w-full px-4 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 hover:scale-105 hover:shadow-xl">
                  🔥 TRANSFORM NOW
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ⚔️ LIVE DEPARTMENT BATTLES - ENHANCED */}
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl animate-pulse">
              <Sword className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-4xl font-black text-transparent bg-gradient-to-r from-gray-800 via-red-600 to-orange-600 bg-clip-text">
                ⚔️ LIVE DEPARTMENT WARFARE
              </h3>
              <p className="text-lg font-bold text-red-600">Real-time corporate wellness battles happening now</p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl blur-sm animate-pulse"></div>
            <div className="relative flex items-center px-6 py-3 space-x-3 border border-red-200 bg-red-50 rounded-xl">
              <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-xl font-black text-red-700">2,847 EPIC BATTLES LIVE!</span>
            </div>
          </div>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Sales vs Marketing - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 border-red-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-pink-600">
                      <Trophy className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">SALES vs MARKETING</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-red-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-SALMKT</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="text-lg font-black text-red-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">18 days remaining</div>
                </div>
              </div>
              
              {/* Battle Progress */}
              <div className="space-y-4">
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-red-200 rounded-lg bg-red-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-black text-gray-700">👔 Sales Team</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-red-600 animate-pulse">94,250</div>
                      <div className="text-xs font-semibold text-gray-600">847 warriors</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">⚡</div>
                </div>
                
                <div className="text-lg font-black text-center text-red-600 animate-pulse">VS</div>
                
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">🎨 Marketing Team</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">89,420</div>
                    <div className="text-xs font-semibold text-gray-600">623 warriors</div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-red-600">Sales Leading</span>
                    <span className="text-gray-600">Gap: 4,830</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-red-500 to-pink-500 animate-pulse" style={{width: '51%'}}></div>
                  </div>
                </div>
                
                {/* Prize Banner */}
                <div className="relative p-4 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                  <div className="absolute p-1 rounded-full -top-2 -right-2 bg-amber-500">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-amber-800">🏆 VICTORY REWARDS</div>
                    <div className="text-xs font-semibold text-amber-700">$15K Team Bonus + Premium Gym Memberships + Health Retreats</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* IT vs Operations - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 border-blue-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600">
                      <Zap className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">IT vs OPERATIONS</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-blue-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-ITOPS</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <div className="text-lg font-black text-blue-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">12 days remaining</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-blue-200 rounded-lg bg-blue-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="font-black text-gray-700">💻 IT Department</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-blue-600 animate-pulse">91,850</div>
                      <div className="text-xs font-semibold text-gray-600">634 tech warriors</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">💻</div>
                </div>
                
                <div className="text-lg font-black text-center text-blue-600 animate-pulse">VS</div>
                
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">⚙️ Operations</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">88,920</div>
                    <div className="text-xs font-semibold text-gray-600">567 ops heroes</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-blue-600">IT Leading</span>
                    <span className="text-gray-600">Gap: 2,930</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse" style={{width: '52%'}}></div>
                  </div>
                </div>
                
                <div className="relative p-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                  <div className="absolute p-1 bg-blue-500 rounded-full -top-2 -right-2">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-blue-800">⚡ TECH DOMINANCE PRIZE</div>
                    <div className="text-xs font-semibold text-blue-700">$12K Tech Equipment Fund + Latest Gadgets + Skills Training</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Doctors vs Nurses - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 border-green-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600">
                      <Heart className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">DOCTORS vs NURSES</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-green-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-DOCNUR</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <div className="text-lg font-black text-green-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">25 days remaining</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">👨‍⚕️ Doctors</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">87,340</div>
                    <div className="text-xs font-semibold text-gray-600">234 medical pros</div>
                  </div>
                </div>
                
                <div className="text-lg font-black text-center text-green-600 animate-pulse">VS</div>
                
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-green-200 rounded-lg bg-green-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-black text-gray-700">👩‍⚕️ Nurses</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-green-600 animate-pulse">92,180</div>
                      <div className="text-xs font-semibold text-gray-600">456 care heroes</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">❤️</div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-green-600">Nurses Leading</span>
                    <span className="text-gray-600">Gap: 4,840</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 animate-pulse" style={{width: '51%'}}></div>
                  </div>
                </div>
                
                <div className="relative p-4 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                  <div className="absolute p-1 bg-green-500 rounded-full -top-2 -right-2">
                    <Heart className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-green-800">💚 HEALTHCARE HEROES PRIZE</div>
                    <div className="text-xs font-semibold text-green-700">Wellness Retreat + Medical Equipment + Mental Health Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Engineering vs Product - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 border-purple-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600">
                      <Code className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">ENG vs PRODUCT</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-purple-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-ENGPRD</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    <div className="text-lg font-black text-purple-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">8 days remaining</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-purple-200 rounded-lg bg-purple-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                      <span className="font-black text-gray-700">⚙️ Engineering</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-purple-600 animate-pulse">96,420</div>
                      <div className="text-xs font-semibold text-gray-600">1,245 code warriors</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">🚀</div>
                </div>
                
                <div className="text-lg font-black text-center text-purple-600 animate-pulse">VS</div>
                
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">📊 Product Team</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">93,180</div>
                    <div className="text-xs font-semibold text-gray-600">867 product ninjas</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-purple-600">Engineering Dominating</span>
                    <span className="text-gray-600">Gap: 3,240</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 animate-pulse" style={{width: '52%'}}></div>
                  </div>
                </div>
                
                <div className="relative p-4 border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
                  <div className="absolute p-1 bg-purple-500 rounded-full -top-2 -right-2">
                    <Code className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-purple-800">🚀 INNOVATION SUPREMACY</div>
                    <div className="text-xs font-semibold text-purple-700">$20K Innovation Budget + Cutting-edge Tools + Skills Mastery</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Finance vs Legal - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 shadow-lg border-amber-500 rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600">
                      <DollarSign className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">FINANCE vs LEGAL</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-amber-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-FINLEG</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                    <div className="text-lg font-black text-amber-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">21 days remaining</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">💰 Finance Team</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">78,960</div>
                    <div className="text-xs font-semibold text-gray-600">345 money masters</div>
                  </div>
                </div>
                
                <div className="text-lg font-black text-center text-amber-600 animate-pulse">VS</div>
                
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border rounded-lg bg-amber-50 border-amber-200 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></div>
                      <span className="font-black text-gray-700">⚖️ Legal Team</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-amber-600 animate-pulse">81,240</div>
                      <div className="text-xs font-semibold text-gray-600">198 legal eagles</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">⚖️</div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-amber-600">Legal Leading</span>
                    <span className="text-gray-600">Gap: 2,280</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 animate-pulse" style={{width: '51%'}}></div>
                  </div>
                </div>
                
                <div className="relative p-4 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                  <div className="absolute p-1 rounded-full -top-2 -right-2 bg-amber-500">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-amber-800">💼 PROFESSIONAL EXCELLENCE</div>
                    <div className="text-xs font-semibold text-amber-700">$10K Professional Development + Executive Training + Certification</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* HR vs Admin - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border-l-4 border-pink-500 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl blur-sm"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600">
                      <Users className="w-6 h-6 text-white animate-bounce" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-800">HR vs ADMIN</h4>
                    <p className="flex items-center space-x-1 text-sm font-bold text-pink-600">
                      <Code className="w-4 h-4" />
                      <span>CORP2025-HRADM</span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                    <div className="text-lg font-black text-pink-600">LIVE</div>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">14 days remaining</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-pink-200 rounded-lg bg-pink-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-pink-500 rounded-full animate-pulse"></div>
                      <span className="font-black text-gray-700">👥 HR Department</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-pink-600 animate-pulse">84,750</div>
                      <div className="text-xs font-semibold text-gray-600">267 people champions</div>
                    </div>
                  </div>
                  <div className="absolute text-2xl transform -translate-y-1/2 -right-2 top-1/2 animate-bounce">👥</div>
                </div>
                
                <div className="text-lg font-black text-center text-pink-600 animate-pulse">VS</div>
                
                <div className="flex items-center justify-between p-4 transition-all duration-300 transform border border-gray-200 rounded-lg bg-gray-50 hover:scale-105">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full animate-pulse"></div>
                    <span className="font-black text-gray-700">📋 Administration</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-gray-600 animate-pulse">79,320</div>
                    <div className="text-xs font-semibold text-gray-600">189 admin experts</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-pink-600">HR Leading</span>
                    <span className="text-gray-600">Gap: 5,430</span>
                  </div>
                  <div className="w-full h-4 overflow-hidden bg-gray-200 rounded-full">
                    <div className="h-full transition-all duration-1000 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 animate-pulse" style={{width: '52%'}}></div>
                  </div>
                </div>
                
                <div className="relative p-4 border border-pink-200 rounded-lg bg-gradient-to-r from-pink-50 to-rose-50">
                  <div className="absolute p-1 bg-pink-500 rounded-full -top-2 -right-2">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-pink-800">👥 PEOPLE POWER PRIZE</div>
                    <div className="text-xs font-semibold text-pink-700">$8K Team Building Budget + Leadership Training + Wellness Programs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Battle Stats Summary */}
        <div className="p-6 border border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50 rounded-2xl">
          <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4">
            <div className="p-3 border border-red-200 rounded-lg bg-gradient-to-r from-red-50 to-pink-50">
              <div className="text-2xl font-black text-red-600 animate-pulse">2,847</div>
              <div className="text-sm font-bold text-red-500">Active Battles</div>
            </div>
            <div className="p-3 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
              <div className="text-2xl font-black text-blue-600 animate-pulse">47,629</div>
              <div className="text-sm font-bold text-blue-500">Warriors Fighting</div>
            </div>
            <div className="p-3 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
              <div className="text-2xl font-black text-amber-600 animate-pulse">$89K</div>
              <div className="text-sm font-bold text-amber-500">Total Prize Pool</div>
            </div>
            <div className="p-3 border rounded-lg bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
              <div className="text-2xl font-black text-emerald-600 animate-pulse">96%</div>
              <div className="text-sm font-bold text-emerald-500">Engagement Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* 🏅 ATTRACTIVE REWARD STRUCTURE */}
      <div className="p-8 border bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-amber-200 rounded-3xl">
        <div className="mb-8 text-center">
          <h3 className="mb-2 text-3xl font-black text-transparent bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text">
            🏅 ATTRACTIVE REWARD STRUCTURE
          </h3>
          <p className="font-medium text-amber-700">Premium rewards that drive maximum engagement and participation</p>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">GYM MEMBERSHIPS</h4>
            </div>
            <p className="mb-2 text-gray-600">1-year premium gym memberships for challenge winners across our partner fitness centers.</p>
            <div className="text-sm font-semibold text-purple-600">🏆 Available for all competition winners</div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">INSURANCE DISCOUNTS</h4>
            </div>
            <p className="mb-2 text-gray-600">15-25% premium reductions for active participants through our insurance partnerships.</p>
            <div className="text-sm font-semibold text-green-600">💡 Automatic enrollment for active users</div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">WELLNESS STIPENDS</h4>
            </div>
            <p className="mb-2 text-gray-600">$500+ wellness spending allowances for health apps, equipment, and services.</p>
            <div className="text-sm font-semibold text-blue-600">🎁 Department winners get priority access</div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">HEALTH DEVICES</h4>
            </div>
            <p className="mb-2 text-gray-600">Smart scales, fitness trackers, sleep monitors, and other premium health devices.</p>
            <div className="text-sm font-semibold text-red-600">📱 Latest technology for top performers</div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-teal-500 to-green-600 rounded-xl">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">SPA & WELLNESS</h4>
            </div>
            <p className="mb-2 text-gray-600">Mental health and recovery rewards including spa treatments and wellness retreats.</p>
            <div className="text-sm font-semibold text-teal-600">🧘 Focus on mental wellness and recovery</div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-amber-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-black text-gray-800">PREMIUM PACKAGES</h4>
            </div>
            <p className="mb-2 text-gray-600">Exclusive wellness packages combining multiple benefits for ultimate winners.</p>
            <div className="text-sm font-semibold text-amber-600">👑 VIP treatment for championship winners</div>
          </div>
        </div>
      </div>

      {/* 🔐 SECURE COMPANY REGISTRATION SYSTEM */}
      <div className="p-8 border border-blue-200 bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 rounded-3xl">
        <div className="mb-8 text-center">
          <h3 className="mb-2 text-3xl font-black text-transparent bg-gradient-to-r from-slate-600 to-blue-600 bg-clip-text">
            🔐 SECURE COMPANY REGISTRATION SYSTEM
          </h3>
          <p className="font-medium text-slate-600">Enterprise-grade security with automatic code generation for all challenge types</p>
        </div>
        
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="p-6 bg-white border border-blue-200 shadow-lg rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-black text-gray-800">COMPANY REGISTRATION FLOW</h4>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-6 h-6 text-sm font-bold text-white bg-blue-500 rounded-full">1</div>
                <div>
                  <p className="font-semibold text-gray-800">Company Verification</p>
                  <p className="text-sm text-gray-600">Business license and employee verification process</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-6 h-6 text-sm font-bold text-white bg-blue-500 rounded-full">2</div>
                <div>
                  <p className="font-semibold text-gray-800">Department Setup</p>
                  <p className="text-sm text-gray-600">Configure departments and assign managers</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-6 h-6 text-sm font-bold text-white bg-blue-500 rounded-full">3</div>
                <div>
                  <p className="font-semibold text-gray-800">Code Generation</p>
                  <p className="text-sm text-gray-600">Automatic secure codes for all challenge types</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6 bg-white border shadow-lg border-emerald-200 rounded-2xl">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-black text-gray-800">SECURE ACCESS CONTROL</h4>
            </div>
            <div className="space-y-4">
              <div className="p-3 border rounded-lg bg-emerald-50 border-emerald-200">
                <p className="font-semibold text-emerald-800">Company-Wide Codes</p>
                <p className="text-sm text-emerald-600">Format: COMPANY2025-ALL</p>
                <p className="text-xs text-emerald-500">All employees can join with main company code</p>
              </div>
              <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                <p className="font-semibold text-blue-800">Department Codes</p>
                <p className="text-sm text-blue-600">Format: COMPANY2025-DEPT</p>
                <p className="text-xs text-blue-500">Department-specific access for targeted challenges</p>
              </div>
              <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                <p className="font-semibold text-purple-800">Battle Codes</p>
                <p className="text-sm text-purple-600">Format: COMPANY2025-DEPTVS</p>
                <p className="text-xs text-purple-500">Special codes for department vs department battles</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 CALL-TO-ACTION - CREATE YOUR CORPORATE CHALLENGE */}
      <div className="p-8 text-center text-white shadow-2xl bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 rounded-3xl">
        <div className="max-w-4xl mx-auto space-y-6">
          <h3 className="mb-4 text-4xl font-black">
            🚀 READY TO LAUNCH YOUR CORPORATE WELLNESS EMPIRE?
          </h3>
          
          <p className="text-xl font-medium text-blue-100">
            Join 1,247 companies already dominating our corporate wellness championships. Create secure challenges, 
            track real ROI, and transform your workforce into wellness warriors.
          </p>
          
          <div className="grid gap-4 text-center md:grid-cols-4">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
              <div className="mb-1 text-2xl font-black text-white">1,247</div>
              <div className="text-sm text-blue-100">Partner Companies</div>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
              <div className="mb-1 text-2xl font-black text-white">89,476</div>
              <div className="text-sm text-blue-100">Active Employees</div>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
              <div className="mb-1 text-2xl font-black text-white">$2.1M</div>
              <div className="text-sm text-blue-100">Rewards Distributed</div>
            </div>
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
              <div className="mb-1 text-2xl font-black text-white">94%</div>
              <div className="text-sm text-blue-100">Satisfaction Rate</div>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 font-bold text-blue-700 transition-all duration-300 transform bg-white shadow-lg rounded-xl hover:bg-blue-50 hover:scale-105">
              🏢 CREATE COMPANY CHALLENGE
            </button>
            <button className="px-8 py-4 font-bold text-white transition-all duration-300 transform shadow-lg bg-gradient-to-r from-amber-500 to-yellow-600 rounded-xl hover:from-amber-600 hover:to-yellow-700 hover:scale-105">
              👑 UPGRADE TO GOLD MEMBERSHIP
            </button>
            <button className="px-8 py-4 font-bold text-white transition-all duration-300 transform shadow-lg bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl hover:from-emerald-600 hover:to-green-700 hover:scale-105">
              ⚔️ START DEPARTMENT BATTLE
            </button>
          </div>
          
          <p className="text-sm text-blue-200">
            💡 <span className="font-semibold">Gold Membership Required</span> ($60/year) • Automatic secure code generation • Enterprise-grade analytics
          </p>
        </div>
      </div>

      {/* 🚀 COMING SOON IN FUTURE PHASES - Corporate */}
      <div className="relative p-8 overflow-hidden border-2 border-dashed rounded-3xl border-emerald-300/60 bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-teal-50/80">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/5 via-green-400/5 to-teal-400/5 animate-pulse"></div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 shadow-xl bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl animate-bounce">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <h3 className="mb-3 text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600">
            🚀 Coming Soon in Future Phases
          </h3>
          <p className="max-w-3xl mx-auto mb-4 text-lg font-semibold text-gray-700">
            Enterprise wellness analytics dashboard, automated ROI reporting, industry benchmarking, 
            executive health scorecards, and corporate wellness consulting services!
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-sm font-bold text-emerald-700">
            <span className="px-3 py-1 rounded-full bg-emerald-100/80">📊 Analytics Dashboard</span>
            <span className="px-3 py-1 rounded-full bg-green-100/80">💰 ROI Calculator</span>
            <span className="px-3 py-1 rounded-full bg-teal-100/80">🏢 Industry Benchmarks</span>
            <span className="px-3 py-1 rounded-full bg-emerald-100/80">👔 Executive Reports</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamsGuilds = () => (
    <div className="space-y-8">
      {/* 🛡️ TEAMS & GUILDS HEADER - REFINED */}
      <div className="relative p-6 overflow-hidden border border-gray-200 shadow-lg bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 rounded-2xl">
        {/* Subtle Background Elements */}
        <div className="absolute top-0 right-0 w-32 h-32 translate-x-16 -translate-y-16 rounded-full bg-gradient-to-bl from-blue-400/10 to-cyan-400/5"></div>
        <div className="absolute bottom-0 left-0 -translate-x-16 translate-y-16 rounded-full w-28 h-28 bg-gradient-to-tr from-purple-400/10 to-indigo-400/5"></div>
        
        {/* Elegant Floating Icons */}
        <div className="absolute top-4 left-4">
          <Users className="w-4 h-4 text-blue-500 opacity-40 animate-pulse" />
        </div>
        <div className="absolute top-4 right-4">
          <Crown className="w-4 h-4 text-yellow-500 opacity-40 animate-pulse" />
        </div>
        
        <div className="relative z-10 space-y-4 text-center">
          <div className="flex items-center justify-center space-x-4">
            <div className="p-3 shadow-md bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-transparent bg-gradient-to-r from-gray-800 via-blue-600 to-cyan-600 bg-clip-text">
                🛡️ Teams & Guilds
              </h1>
              <h2 className="text-lg font-semibold text-transparent bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text">
                Global Wellness Competition
              </h2>
            </div>
          </div>
          
          <p className="max-w-2xl mx-auto text-base font-medium text-gray-600">
            Connect with elite wellness warriors worldwide • Challenge cities and countries • Climb global rankings
          </p>
          
          {/* Compact Live Stats */}
          <div className="flex items-center justify-center py-2 space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-green-600">6.8M+ Active</span>
            </div>
            <div className="w-px h-4 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-blue-600">156 Cities</span>
            </div>
            <div className="w-px h-4 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-purple-600">847 Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* 🏙️ LIVE CITY BATTLES - MODERN & ELEGANT */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-gray-800 via-blue-600 to-cyan-600 bg-clip-text">
                🏙️ Live City Battles
              </h3>
              <p className="text-sm font-medium text-blue-600">Real-time urban wellness competition</p>
            </div>
          </div>
          <div className="flex items-center px-4 py-2 space-x-2 border border-blue-200 rounded-lg bg-blue-50">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-bold text-blue-700">Live</span>
          </div>
        </div>
        
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {geographicalBattles && Array.isArray(geographicalBattles) && geographicalBattles.map((battle, index) => {
            // Safety check for battle object
            if (!battle || typeof battle !== 'object') {
              console.warn(`Battle at index ${index} is invalid:`, battle);
              return null;
            }
            
            if (!battle.title) {
              console.warn(`Battle at index ${index} missing title:`, battle);
              return null;
            }
            
            console.log(`Processing battle ${index}:`, battle.title);
            const parsedBattle = parseBattleTitle(battle.title);
            if (!parsedBattle) {
              console.warn(`Could not parse battle title at index ${index}:`, battle.title);
              return null;
            }
            
            // Add duration and prize to battle object
            const battleWithDefaults = {
              ...battle,
              duration: "7 days",
              prize: typeof battle.prizes?.first === 'object' ? battle.prizes.first.name : "$50,000 Prize Pool"
            };

            // Unique 3D color themes for each battle card - ultra transparent and refined
            const battle3DThemes = [
              {
                name: 'Royal Blue',
                headerGradient: 'from-blue-300/70 via-indigo-400/70 to-purple-500/70',
                bgGradient: 'from-blue-50/50 via-indigo-50/30 to-purple-100/50',
                borderColor: 'border-blue-300/50',
                shadowColor: 'shadow-blue-400/15',
                textColor: 'text-blue-900',
                accentColor: 'text-blue-800',
                buttonGradient: 'from-blue-500/80 via-indigo-600/80 to-purple-700/80',
                glowColor: 'bg-blue-400'
              },
              {
                name: 'Emerald Green',
                headerGradient: 'from-emerald-300/70 via-green-400/70 to-teal-500/70',
                bgGradient: 'from-emerald-50/50 via-green-50/30 to-teal-100/50',
                borderColor: 'border-emerald-300/50',
                shadowColor: 'shadow-emerald-400/15',
                textColor: 'text-emerald-900',
                accentColor: 'text-emerald-800',
                buttonGradient: 'from-emerald-500/80 via-green-600/80 to-teal-700/80',
                glowColor: 'bg-emerald-400'
              },
              {
                name: 'Sunset Orange',
                headerGradient: 'from-orange-300/70 via-red-400/70 to-pink-500/70',
                bgGradient: 'from-orange-50/50 via-red-50/30 to-pink-100/50',
                borderColor: 'border-orange-300/50',
                shadowColor: 'shadow-orange-400/15',
                textColor: 'text-orange-900',
                accentColor: 'text-orange-800',
                buttonGradient: 'from-orange-500/80 via-red-600/80 to-pink-700/80',
                glowColor: 'bg-orange-400'
              },
              {
                name: 'Deep Purple',
                headerGradient: 'from-purple-300/70 via-violet-400/70 to-fuchsia-500/70',
                bgGradient: 'from-purple-50/50 via-violet-50/30 to-fuchsia-100/50',
                borderColor: 'border-purple-300/50',
                shadowColor: 'shadow-purple-400/15',
                textColor: 'text-purple-900',
                accentColor: 'text-purple-800',
                buttonGradient: 'from-purple-500/80 via-violet-600/80 to-fuchsia-700/80',
                glowColor: 'bg-purple-400'
              },
              {
                name: 'Golden Amber',
                headerGradient: 'from-amber-300/70 via-yellow-400/70 to-orange-500/70',
                bgGradient: 'from-amber-50/50 via-yellow-50/30 to-orange-100/50',
                borderColor: 'border-amber-300/50',
                shadowColor: 'shadow-amber-400/15',
                textColor: 'text-amber-900',
                accentColor: 'text-amber-800',
                buttonGradient: 'from-amber-500/80 via-yellow-600/80 to-orange-700/80',
                glowColor: 'bg-amber-400'
              },
              {
                name: 'Cyan Blue',
                headerGradient: 'from-cyan-300/70 via-sky-400/70 to-blue-500/70',
                bgGradient: 'from-cyan-50/50 via-sky-50/30 to-blue-100/50',
                borderColor: 'border-cyan-300/50',
                shadowColor: 'shadow-cyan-400/15',
                textColor: 'text-cyan-900',
                accentColor: 'text-cyan-800',
                buttonGradient: 'from-cyan-500/80 via-sky-600/80 to-blue-700/80',
                glowColor: 'bg-cyan-400'
              },
              {
                name: 'Rose Pink',
                headerGradient: 'from-rose-300/70 via-pink-400/70 to-fuchsia-500/70',
                bgGradient: 'from-rose-50/50 via-pink-50/30 to-fuchsia-100/50',
                borderColor: 'border-rose-300/50',
                shadowColor: 'shadow-rose-400/15',
                textColor: 'text-rose-900',
                accentColor: 'text-rose-800',
                buttonGradient: 'from-rose-500/80 via-pink-600/80 to-fuchsia-700/80',
                glowColor: 'bg-rose-400'
              },
              {
                name: 'Forest Green',
                headerGradient: 'from-green-400/70 via-emerald-500/70 to-teal-600/70',
                bgGradient: 'from-green-50/50 via-emerald-50/30 to-teal-100/50',
                borderColor: 'border-green-300/50',
                shadowColor: 'shadow-green-400/15',
                textColor: 'text-green-900',
                accentColor: 'text-green-800',
                buttonGradient: 'from-green-600/80 via-emerald-700/80 to-teal-800/80',
                glowColor: 'bg-green-400'
              }
            ];
            
            const theme = battle3DThemes[index % battle3DThemes.length];
            
            return (
            <div key={battle.id || Math.random()} className="group perspective-1000">
              <div className={`
                relative overflow-hidden bg-gradient-to-b ${theme.bgGradient} rounded-3xl 
                shadow-lg ${theme.shadowColor} hover:shadow-xl transition-all duration-700 
                border-2 ${theme.borderColor} cursor-pointer transform-gpu
                hover:scale-110 hover:-translate-y-6 hover:rotate-y-12 hover:rotate-x-5
                before:absolute before:inset-0 before:bg-gradient-to-t before:from-black/5 before:to-transparent 
                before:rounded-3xl before:opacity-0 before:transition-opacity before:duration-500 hover:before:opacity-100
              `}
                   onClick={() => setSelectedBattle(battle)}>
              
                {/* 3D Epic Battle Header */}
                <div className={`relative p-6 bg-gradient-to-br ${theme.headerGradient} text-white overflow-hidden backdrop-blur-sm`}>
                  {/* 3D Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500"></div>
                  
                  {/* 3D Battle Arena Display */}
                  <div className="relative z-10">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      {/* 3D Challenger with Depth */}
                      <div className="text-center transition-all duration-500 group-hover:scale-110 group-hover:-translate-y-1">
                        <div className="relative mb-2">
                          <div className="flex items-center justify-center w-12 h-12 transition-all duration-300 transform border shadow-xl bg-white/20 backdrop-blur-xl rounded-xl border-white/30 hover:rotate-6">
                            <span className="text-2xl drop-shadow-lg">{parsedBattle.challenger.flag}</span>
                          </div>
                          {/* 3D Glow Ring */}
                          <div className="absolute transition-all duration-500 opacity-0 -inset-1 bg-white/10 rounded-xl blur-md group-hover:opacity-50"></div>
                          {/* 3D Shadow */}
                          <div className="absolute w-12 h-12 top-1 left-1 bg-black/20 rounded-xl blur-sm -z-10"></div>
                        </div>
                        <h5 className="text-xs font-bold tracking-wide uppercase opacity-90 drop-shadow-lg">
                          {parsedBattle.challenger.name}
                        </h5>
                        <div className="w-full h-px mt-1 bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
                      </div>

                      {/* 3D Epic VS Badge */}
                      <div className="relative">
                        <div className="flex items-center justify-center w-10 h-10 transition-all duration-300 transform border shadow-xl bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-600 rounded-xl hover:scale-110 hover:rotate-6 border-amber-300/50">
                          <span className="text-sm font-black tracking-wider text-white drop-shadow-lg">VS</span>
                        </div>
                        {/* 3D Pulse Ring */}
                        <div className="absolute inset-0 scale-125 bg-gradient-to-r from-amber-400/30 to-orange-400/30 rounded-xl animate-pulse blur-md"></div>
                        {/* 3D Energy Bolts */}
                        <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
                      </div>

                      {/* 3D Defender with Depth */}
                      <div className="text-center transition-all duration-500 group-hover:scale-110 group-hover:-translate-y-1">
                        <div className="relative mb-2">
                          <div className="flex items-center justify-center w-12 h-12 transition-all duration-300 transform border shadow-xl bg-white/20 backdrop-blur-xl rounded-xl border-white/30 hover:rotate-6">
                            <span className="text-2xl drop-shadow-lg">{parsedBattle.defender.flag}</span>
                          </div>
                          <div className="absolute transition-all duration-500 opacity-0 -inset-1 bg-white/10 rounded-xl blur-md group-hover:opacity-50"></div>
                          <div className="absolute w-12 h-12 top-1 left-1 bg-black/20 rounded-xl blur-sm -z-10"></div>
                        </div>
                        <h5 className="text-xs font-bold tracking-wide uppercase opacity-90 drop-shadow-lg">
                          {parsedBattle.defender.name}
                        </h5>
                        <div className="w-full h-px mt-1 bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
                      </div>
                    </div>
                    
                    {/* 3D Championship Title */}
                    <div className="text-center">
                      <h4 className="text-sm font-bold tracking-wide text-white drop-shadow-lg">
                        {parsedBattle.displayTitle}
                      </h4>
                      <div className="w-24 h-px mx-auto mt-2 bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                    </div>
                  </div>
                </div>
                
                {/* 3D Elite Battle Stats */}
                <div className="relative p-4">
                  {/* 3D Stats Cards */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* 3D Duration Card */}
                    <div className="relative p-3 transition-all duration-300 border shadow-sm bg-white/40 backdrop-blur-md rounded-xl border-white/20 hover:shadow-md hover:scale-105 transform-gpu">
                      <div className="relative z-10 text-center">
                        <div className={`w-8 h-8 mx-auto mb-2 rounded-lg bg-gradient-to-r ${theme.buttonGradient} flex items-center justify-center shadow-md`}>
                          <Clock className="w-4 h-4 text-white drop-shadow-sm" />
                        </div>
                        <p className="mb-1 text-xs font-bold tracking-wide text-gray-600 uppercase">Duration</p>
                        <p className={`text-sm font-black ${theme.accentColor} drop-shadow-sm`}>{battle.timeLeft || "7d"}</p>
                      </div>
                      {/* 3D Card Shadow */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/3 rounded-xl"></div>
                    </div>
                    
                    {/* 3D Participants Card */}
                    <div className="relative p-3 transition-all duration-300 border shadow-sm bg-white/40 backdrop-blur-md rounded-xl border-white/20 hover:shadow-md hover:scale-105 transform-gpu">
                      <div className="relative z-10 text-center">
                        <div className={`w-8 h-8 mx-auto mb-2 rounded-lg bg-gradient-to-r ${theme.buttonGradient} flex items-center justify-center shadow-md`}>
                          <Users className="w-4 h-4 text-white drop-shadow-sm" />
                        </div>
                        <p className="mb-1 text-xs font-bold tracking-wide text-gray-600 uppercase">Warriors</p>
                        <p className={`text-sm font-black ${theme.accentColor} drop-shadow-sm`}>{(battleWithDefaults.participants / 1000).toFixed(0)}K</p>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/3 rounded-xl"></div>
                    </div>
                  </div>

                  {/* 3D Epic Prize Display */}
                  <div className="relative p-4 mb-4 overflow-hidden border shadow-sm bg-gradient-to-br from-amber-50/40 via-yellow-50/40 to-orange-100/40 rounded-2xl border-amber-200/20">
                    {/* 3D Shine sweep */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    <div className="relative z-10 text-center">
                      <p className="mb-1 text-xs font-bold tracking-wide uppercase text-amber-800 drop-shadow-sm">🏆 Victory Reward</p>
                      <p className="text-sm font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-700 via-orange-600 to-red-700 drop-shadow-sm">
                        {battleWithDefaults.prize}
                      </p>
                    </div>
                    {/* 3D Corner Gems - ultra subtle */}
                    <div className="absolute w-1 h-1 rounded-full top-1 left-1 bg-yellow-400/40 blur-sm animate-pulse opacity-30"></div>
                    <div className="absolute w-1 h-1 rounded-full top-1 right-1 bg-amber-300/40 blur-sm animate-pulse opacity-30" style={{animationDelay: '0.3s'}}></div>
                    <div className="absolute w-1 h-1 rounded-full bottom-1 left-1 bg-orange-400/40 blur-sm animate-pulse opacity-30" style={{animationDelay: '0.6s'}}></div>
                    <div className="absolute w-1 h-1 rounded-full bottom-1 right-1 bg-red-400/40 blur-sm animate-pulse opacity-30" style={{animationDelay: '0.9s'}}></div>
                  </div>
                  
                  {/* 3D Epic Battle Button */}
                  <button className={`group/btn relative w-full bg-gradient-to-r ${theme.buttonGradient} text-white py-3 px-6 rounded-2xl font-bold text-xs uppercase tracking-wide transition-all duration-500 shadow-xl hover:shadow-2xl transform hover:scale-105 hover:-translate-y-1 overflow-hidden`}>
                    {/* 3D Button Shine */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover/btn:translate-x-[200%] transition-transform duration-700"></div>
                    <div className="relative z-10 flex items-center justify-center space-x-2">
                      <span className="drop-shadow-sm">⚔️ Enter Battle</span>
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                    </div>
                    {/* 3D Button Shadow */}
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl opacity-50`}></div>
                  </button>
                </div>

                {/* 3D Card Edge Highlights */}
                <div className="absolute w-6 h-6 border-t-2 border-l-2 top-4 left-4 border-white/40 rounded-tl-2xl"></div>
                <div className="absolute w-6 h-6 border-t-2 border-r-2 top-4 right-4 border-white/40 rounded-tr-2xl"></div>
                <div className="absolute w-6 h-6 border-b-2 border-l-2 bottom-4 left-4 border-white/40 rounded-bl-2xl"></div>
                <div className="absolute w-6 h-6 border-b-2 border-r-2 bottom-4 right-4 border-white/40 rounded-br-2xl"></div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* ⚔️ CHALLENGE CREATION SYSTEM - ENHANCED */}
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl animate-pulse">
              <Sword className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-gray-800 via-purple-600 to-indigo-600 bg-clip-text">
                ⚔️ Challenge Creator
              </h3>
              <p className="text-sm font-medium text-purple-600">Design global wellness competitions</p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur-sm animate-pulse"></div>
            <div className="relative flex items-center px-6 py-3 space-x-3 border border-purple-200 bg-purple-50 rounded-xl">
              <Sword className="w-4 h-4 text-purple-500 animate-pulse" />
              <span className="text-xl font-black text-purple-700">BATTLE CREATOR ACTIVE!</span>
            </div>
          </div>
        </div>
        
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Country Challenges - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-3">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl blur-sm"></div>
                  <div className="relative p-3 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-xl">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h4 className="text-xl font-black text-gray-800">COUNTRY WARS</h4>
              </div>
              
              <div className="mb-6 space-y-3">
                {availableChallenges.countries.slice(0, 3).map((country, index) => (
                  <div key={index} className="flex items-center justify-between p-3 transition-all duration-300 transform border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl animate-bounce" style={{animationDelay: `${index * 0.2}s`}}>{country.flag}</span>
                      <span className="text-sm font-black text-gray-700">{country.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-amber-600">{(country.members / 1000000).toFixed(1)}M</div>
                      <div className="text-xs font-semibold text-amber-500">warriors</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Stats Bar */}
              <div className="p-3 mb-4 border rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-200">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-amber-700">Available Nations</span>
                  <span className="text-amber-800">{availableChallenges.countries.length}</span>
                </div>
                <div className="w-full h-2 mt-2 rounded-full bg-amber-200">
                  <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 animate-pulse" style={{width: '78%'}}></div>
                </div>
              </div>
              
              <button className="w-full px-4 py-4 text-sm font-bold text-white transition-all duration-300 transform shadow-lg rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 hover:scale-105 hover:shadow-xl">
                <div className="flex items-center justify-center space-x-2">
                  <Trophy className="w-4 h-4" />
                  <span>🌍 CHALLENGE COUNTRY</span>
                </div>
              </button>
            </div>
          </div>

          {/* City Challenges - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-3">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur-sm"></div>
                  <div className="relative p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h4 className="text-xl font-black text-gray-800">CITY BATTLES</h4>
              </div>
              
              <div className="mb-6 space-y-3">
                {availableChallenges.cities.slice(0, 3).map((city, index) => (
                  <div key={index} className="flex items-center justify-between p-3 transition-all duration-300 transform border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl animate-bounce" style={{animationDelay: `${index * 0.2}s`}}>{city.flag}</span>
                      <span className="text-sm font-black text-gray-700">{city.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-blue-600">{(city.members / 1000).toFixed(0)}K</div>
                      <div className="text-xs font-semibold text-blue-500">warriors</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 mb-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-100 to-indigo-100">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-blue-700">Available Cities</span>
                  <span className="text-blue-800">{availableChallenges.cities.length}</span>
                </div>
                <div className="w-full h-2 mt-2 bg-blue-200 rounded-full">
                  <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 animate-pulse" style={{width: '85%'}}></div>
                </div>
              </div>
              
              <button className="w-full px-4 py-4 text-sm font-bold text-white transition-all duration-300 transform shadow-lg rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:scale-105 hover:shadow-xl">
                <div className="flex items-center justify-center space-x-2">
                  <Building2 className="w-4 h-4" />
                  <span>🏙️ CHALLENGE CITY</span>
                </div>
              </button>
            </div>
          </div>

          {/* State Challenges - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-3">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur-sm"></div>
                  <div className="relative p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h4 className="text-xl font-black text-gray-800">STATE WARS</h4>
              </div>
              
              <div className="mb-6 space-y-3">
                {availableChallenges.states.slice(0, 3).map((state, index) => (
                  <div key={index} className="flex items-center justify-between p-3 transition-all duration-300 transform border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl animate-bounce" style={{animationDelay: `${index * 0.2}s`}}>{state.flag}</span>
                      <span className="text-sm font-black text-gray-700">{state.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-600">{(state.members / 1000).toFixed(0)}K</div>
                      <div className="text-xs font-semibold text-green-500">warriors</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 mb-4 border border-green-200 rounded-lg bg-gradient-to-r from-green-100 to-emerald-100">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-green-700">Available States</span>
                  <span className="text-green-800">{availableChallenges.states.length}</span>
                </div>
                <div className="w-full h-2 mt-2 bg-green-200 rounded-full">
                  <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 animate-pulse" style={{width: '72%'}}></div>
                </div>
              </div>
              
              <button className="w-full px-4 py-4 text-sm font-bold text-white transition-all duration-300 transform shadow-lg rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-105 hover:shadow-xl">
                <div className="flex items-center justify-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>🗺️ CHALLENGE STATE</span>
                </div>
              </button>
            </div>
          </div>

          {/* Province Challenges - Enhanced */}
          <div className="relative group">
            <div className="absolute transition duration-500 -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm"></div>
            <div className="relative p-8 transition-all duration-500 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
              <div className="flex items-center mb-6 space-x-3">
                <div className="relative">
                  <div className="absolute opacity-75 -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur-sm"></div>
                  <div className="relative p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                    <Flag className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h4 className="text-xl font-black text-gray-800">PROVINCE BATTLES</h4>
              </div>
              
              <div className="mb-6 space-y-3">
                {availableChallenges.provinces.map((province, index) => (
                  <div key={index} className="flex items-center justify-between p-3 transition-all duration-300 transform border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 hover:scale-105">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl animate-bounce" style={{animationDelay: `${index * 0.2}s`}}>{province.flag}</span>
                      <span className="text-sm font-black text-gray-700">{province.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-purple-600">{(province.members / 1000).toFixed(0)}K</div>
                      <div className="text-xs font-semibold text-purple-500">warriors</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 mb-4 border border-purple-200 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-purple-700">Available Provinces</span>
                  <span className="text-purple-800">{availableChallenges.provinces.length}</span>
                </div>
                <div className="w-full h-2 mt-2 bg-purple-200 rounded-full">
                  <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" style={{width: '64%'}}></div>
                </div>
              </div>
              
              <button className="w-full px-4 py-4 text-sm font-bold text-white transition-all duration-300 transform shadow-lg rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 hover:scale-105 hover:shadow-xl">
                <div className="flex items-center justify-center space-x-2">
                  <Flag className="w-4 h-4" />
                  <span>🍁 CHALLENGE PROVINCE</span>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        {/* Challenge Statistics Summary */}
        <div className="p-6 border border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50 rounded-2xl">
          <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4">
            <div className="p-3 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
              <div className="text-2xl font-black text-amber-600 animate-pulse">{availableChallenges.countries.length}</div>
              <div className="text-sm font-bold text-amber-500">Countries Ready</div>
            </div>
            <div className="p-3 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="text-2xl font-black text-blue-600 animate-pulse">{availableChallenges.cities.length}</div>
              <div className="text-sm font-bold text-blue-500">Cities Available</div>
            </div>
            <div className="p-3 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="text-2xl font-black text-green-600 animate-pulse">{availableChallenges.states.length}</div>
              <div className="text-sm font-bold text-green-500">States Ready</div>
            </div>
            <div className="p-3 border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="text-2xl font-black text-purple-600 animate-pulse">{availableChallenges.provinces.length}</div>
              <div className="text-sm font-bold text-purple-500">Provinces Active</div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Rankings System */}
      <div className="space-y-8">
        {/* Enhanced Regional Powers Overview */}
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-center text-transparent bg-gradient-to-r from-gray-800 to-purple-600 bg-clip-text">
            🌍 Regional Power Structure
          </h3>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {/* North America */}
            <div className="overflow-hidden transition-all duration-300 bg-white border-l-4 border-blue-500 shadow-lg rounded-xl hover:shadow-xl">
              <div className="p-4">
                <h4 className="mb-3 text-lg font-bold text-gray-800">🇺🇸 North America</h4>
                
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-lg font-black text-blue-600">2.8M</div>
                        <div className="text-xs font-semibold text-blue-700">Members</div>
                      </div>
                      <div>
                        <div className="text-lg font-black text-purple-600">95,240</div>
                        <div className="text-xs font-semibold text-purple-700">Power</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="mb-1 text-sm font-bold text-gray-700">Top Countries:</h5>
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold text-gray-600">🇺🇸 USA</div>
                      <div className="font-semibold text-gray-600">🇨🇦 Canada</div>
                      <div className="font-semibold text-gray-600">🇲🇽 Mexico</div>
                    </div>
                  </div>

                  <div className="p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                    <div className="mb-1 text-xs font-bold text-amber-800">Dominant City:</div>
                    <div className="text-sm font-black text-amber-900">New York</div>
                  </div>

                  <div className="p-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="mb-1 text-xs font-bold text-green-800">Specialization:</div>
                    <div className="text-xs font-semibold text-green-700">Corporate Wellness</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Europe */}
            <div className="overflow-hidden transition-all duration-300 bg-white border-l-4 shadow-lg border-emerald-500 rounded-xl hover:shadow-xl">
              <div className="p-4">
                <h4 className="mb-3 text-lg font-bold text-gray-800">🇪🇺 Europe</h4>
                
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-green-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-lg font-black text-emerald-600">2.1M</div>
                        <div className="text-xs font-semibold text-emerald-700">Members</div>
                      </div>
                      <div>
                        <div className="text-lg font-black text-purple-600">87,650</div>
                        <div className="text-xs font-semibold text-purple-700">Power</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="mb-1 text-sm font-bold text-gray-700">Top Countries:</h5>
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold text-gray-600">🇬🇧 UK</div>
                      <div className="font-semibold text-gray-600">🇩🇪 Germany</div>
                      <div className="font-semibold text-gray-600">🇫🇷 France</div>
                    </div>
                  </div>

                  <div className="p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                    <div className="mb-1 text-xs font-bold text-amber-800">Dominant City:</div>
                    <div className="text-sm font-black text-amber-900">London</div>
                  </div>

                  <div className="p-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="mb-1 text-xs font-bold text-green-800">Specialization:</div>
                    <div className="text-xs font-semibold text-green-700">Mental Health</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Asia Pacific */}
            <div className="overflow-hidden transition-all duration-300 bg-white border-l-4 border-purple-500 shadow-lg rounded-xl hover:shadow-xl">
              <div className="p-4">
                <h4 className="mb-3 text-lg font-bold text-gray-800">🇯🇵 Asia Pacific</h4>
                
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-lg font-black text-purple-600">1.9M</div>
                        <div className="text-xs font-semibold text-purple-700">Members</div>
                      </div>
                      <div>
                        <div className="text-lg font-black text-purple-600">82,180</div>
                        <div className="text-xs font-semibold text-purple-700">Power</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="mb-1 text-sm font-bold text-gray-700">Top Countries:</h5>
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold text-gray-600">🇯🇵 Japan</div>
                      <div className="font-semibold text-gray-600">🇦🇺 Australia</div>
                      <div className="font-semibold text-gray-600">🇸🇬 Singapore</div>
                    </div>
                  </div>

                  <div className="p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                    <div className="mb-1 text-xs font-bold text-amber-800">Dominant City:</div>
                    <div className="text-sm font-black text-amber-900">Tokyo</div>
                  </div>

                  <div className="p-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="mb-1 text-xs font-bold text-green-800">Specialization:</div>
                    <div className="text-xs font-semibold text-green-700">Tech Wellness</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Middle East */}
            <div className="overflow-hidden transition-all duration-300 bg-white border-l-4 border-orange-500 shadow-lg rounded-xl hover:shadow-xl">
              <div className="p-4">
                <h4 className="mb-3 text-lg font-bold text-gray-800">🏜️ Middle East</h4>
                
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-orange-50 to-red-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-lg font-black text-orange-600">890K</div>
                        <div className="text-xs font-semibold text-orange-700">Members</div>
                      </div>
                      <div>
                        <div className="text-lg font-black text-purple-600">45,320</div>
                        <div className="text-xs font-semibold text-purple-700">Power</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="mb-1 text-sm font-bold text-gray-700">Top Countries:</h5>
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold text-gray-600">🇦🇪 UAE</div>
                      <div className="font-semibold text-gray-600">🇸🇦 Saudi Arabia</div>
                      <div className="font-semibold text-gray-600">🇮🇱 Israel</div>
                    </div>
                  </div>

                  <div className="p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                    <div className="mb-1 text-xs font-bold text-amber-800">Dominant City:</div>
                    <div className="text-sm font-black text-amber-900">Dubai</div>
                  </div>

                  <div className="p-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="mb-1 text-xs font-bold text-green-800">Specialization:</div>
                    <div className="text-xs font-semibold text-green-700">Luxury Wellness</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Africa */}
            <div className="overflow-hidden transition-all duration-300 bg-white border-l-4 border-teal-500 shadow-lg rounded-xl hover:shadow-xl">
              <div className="p-4">
                <h4 className="mb-3 text-lg font-bold text-gray-800">🌍 Africa</h4>
                
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-lg font-black text-teal-600">720K</div>
                        <div className="text-xs font-semibold text-teal-700">Members</div>
                      </div>
                      <div>
                        <div className="text-lg font-black text-purple-600">38,950</div>
                        <div className="text-xs font-semibold text-purple-700">Power</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="mb-1 text-sm font-bold text-gray-700">Top Countries:</h5>
                    <div className="space-y-1 text-xs">
                      <div className="font-semibold text-gray-600">🇿🇦 South Africa</div>
                      <div className="font-semibold text-gray-600">🇳🇬 Nigeria</div>
                      <div className="font-semibold text-gray-600">🇪🇬 Egypt</div>
                    </div>
                  </div>

                  <div className="p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                    <div className="mb-1 text-xs font-bold text-amber-800">Dominant City:</div>
                    <div className="text-sm font-black text-amber-900">Cape Town</div>
                  </div>

                  <div className="p-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="mb-1 text-xs font-bold text-green-800">Specialization:</div>
                    <div className="text-xs font-semibold text-green-700">Community Health</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact City Championships */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-transparent bg-gradient-to-r from-gray-800 via-cyan-600 to-blue-600 bg-clip-text">
                  🌆 City Championships
                </h3>
                <p className="text-sm font-medium text-cyan-600">Top metropolitan wellness centers</p>
              </div>
            </div>
            <div className="px-4 py-2 border rounded-lg border-cyan-200 bg-cyan-50">
              <span className="text-sm font-bold text-cyan-700">URBAN DOMINANCE</span>
            </div>
          </div>
          
          <div className="overflow-hidden bg-white border border-gray-200 shadow-2xl rounded-3xl">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
              <h4 className="text-lg font-bold text-center text-gray-800">🏙️ TOP 5 WELLNESS CAPITALS</h4>
            </div>
            <div className="p-8 bg-gradient-to-b from-gray-50 to-white">
              {/* Five 3D Cities in One Row */}
              <div className="grid grid-cols-5 gap-6">
                {globalRankings.topCities.map((city, index) => (
                  <div key={index} className="group perspective-1000">
                    <div className={`
                      relative p-6 rounded-3xl border-2 transition-all duration-500 cursor-pointer transform-gpu
                      shadow-2xl hover:shadow-3xl hover:scale-110 hover:-translate-y-4 hover:rotate-y-12
                      ${index === 0 ? 'bg-gradient-to-b from-yellow-100 via-amber-50 to-yellow-200 border-yellow-400 shadow-yellow-500/30' :
                        index === 1 ? 'bg-gradient-to-b from-gray-100 via-slate-50 to-gray-200 border-gray-400 shadow-gray-500/30' :
                        index === 2 ? 'bg-gradient-to-b from-amber-100 via-orange-50 to-amber-200 border-amber-400 shadow-amber-500/30' :
                        index === 3 ? 'bg-gradient-to-b from-cyan-100 via-blue-50 to-cyan-200 border-cyan-400 shadow-cyan-500/30' :
                        'bg-gradient-to-b from-purple-100 via-indigo-50 to-purple-200 border-purple-400 shadow-purple-500/30'
                      }
                      before:absolute before:inset-0 before:bg-gradient-to-t before:from-black/10 before:to-transparent before:rounded-3xl before:opacity-0 before:transition-opacity before:duration-500 hover:before:opacity-100
                    `}>
                      
                      {/* 3D Rank Badge with Floating Effect */}
                      <div className="relative flex justify-center mb-4">
                        <div className={`
                          w-14 h-14 rounded-full flex items-center justify-center text-white font-black text-xl shadow-2xl
                          transform transition-all duration-500 hover:scale-125 hover:-translate-y-2 hover:rotate-12
                          relative z-10
                          ${index === 0 ? 'bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600' :
                            index === 1 ? 'bg-gradient-to-br from-gray-400 via-slate-500 to-gray-600' :
                            index === 2 ? 'bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600' :
                            index === 3 ? 'bg-gradient-to-br from-cyan-400 via-blue-500 to-cyan-600' :
                            'bg-gradient-to-br from-purple-400 via-indigo-500 to-purple-600'
                          }
                        `}>
                          <span className="drop-shadow-lg">{city.rank}</span>
                          
                          {/* 3D Ring Effect */}
                          <div className={`
                            absolute inset-0 rounded-full border-4 opacity-50 scale-150
                            ${index === 0 ? 'border-yellow-300' :
                              index === 1 ? 'border-gray-300' :
                              index === 2 ? 'border-amber-300' :
                              index === 3 ? 'border-cyan-300' :
                              'border-purple-300'
                            }
                          `}></div>
                        </div>
                        
                        {/* Floating Glow Effect */}
                        <div className={`
                          absolute w-16 h-16 rounded-full blur-xl opacity-60 -z-10 transition-all duration-500
                          ${index === 0 ? 'bg-yellow-400' :
                            index === 1 ? 'bg-gray-400' :
                            index === 2 ? 'bg-amber-400' :
                            index === 3 ? 'bg-cyan-400' :
                            'bg-purple-400'
                          }
                        `}></div>
                      </div>
                      
                      {/* 3D Winner Crown */}
                      {index === 0 && (
                        <div className="absolute z-20 transform -translate-x-1/2 -top-3 left-1/2">
                          <div className="relative">
                            <Trophy className="w-8 h-8 text-yellow-500 drop-shadow-xl animate-bounce" />
                            <div className="absolute inset-0 w-8 h-8 bg-yellow-400 rounded-full opacity-50 blur-lg animate-pulse"></div>
                          </div>
                        </div>
                      )}
                      
                      {/* 3D Country Flag with Depth */}
                      <div className="relative mb-4 text-center">
                        <div className="relative inline-block">
                          <div className="mb-3 text-5xl transition-all duration-500 transform hover:scale-125 drop-shadow-2xl">
                            {city.country}
                          </div>
                          {/* Flag Shadow */}
                          <div className="absolute inset-0 text-5xl transform translate-x-1 translate-y-1 text-black/20 -z-10">
                            {city.country}
                          </div>
                        </div>
                      </div>
                      
                      {/* 3D City Name Card */}
                      <div className="mb-5 text-center">
                        <div className="relative">
                          <h5 className={`
                            text-lg font-black mb-3 drop-shadow-lg
                            ${index === 0 ? 'text-yellow-900' :
                              index === 1 ? 'text-gray-900' :
                              index === 2 ? 'text-amber-900' :
                              index === 3 ? 'text-cyan-900' :
                              'text-purple-900'
                            }
                          `}>
                            {city.name}
                          </h5>
                          
                          {/* 3D Region Badge */}
                          <div className={`
                            inline-block px-4 py-2 rounded-full text-sm font-bold text-white shadow-xl
                            transform transition-all duration-300 hover:scale-110
                            ${index === 0 ? 'bg-gradient-to-r from-yellow-600 to-amber-700' :
                              index === 1 ? 'bg-gradient-to-r from-gray-600 to-slate-700' :
                              index === 2 ? 'bg-gradient-to-r from-amber-600 to-orange-700' :
                              index === 3 ? 'bg-gradient-to-r from-cyan-600 to-blue-700' :
                              'bg-gradient-to-r from-purple-600 to-indigo-700'
                            }
                          `}>
                            {city.region}
                          </div>
                        </div>
                      </div>
                      
                      {/* 3D Power Score Display */}
                      <div className="relative mb-4 text-center">
                        <div className={`
                          inline-block p-4 rounded-2xl shadow-xl border-2 bg-white/80 backdrop-blur-sm
                          ${index === 0 ? 'border-yellow-300' :
                            index === 1 ? 'border-gray-300' :
                            index === 2 ? 'border-amber-300' :
                            index === 3 ? 'border-cyan-300' :
                            'border-purple-300'
                          }
                        `}>
                          <div className={`
                            text-2xl font-black drop-shadow-lg
                            ${index === 0 ? 'text-yellow-800' :
                              index === 1 ? 'text-gray-800' :
                              index === 2 ? 'text-amber-800' :
                              index === 3 ? 'text-cyan-800' :
                              'text-purple-800'
                            }
                          `}>
                            {(city.powerScore / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs font-bold tracking-wider text-gray-600 uppercase">Power Score</div>
                        </div>
                      </div>
                      
                      {/* 3D Members Display */}
                      <div className="mb-5 text-center">
                        <div className="p-3 border shadow-lg bg-white/60 backdrop-blur-sm rounded-xl border-white/50">
                          <div className="text-lg font-bold text-gray-800">
                            {(city.members / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs font-medium text-gray-600">Warriors</div>
                        </div>
                      </div>
                      
                      {/* 3D Progress Bar with Depth */}
                      <div className="relative">
                        <div className="w-full h-3 overflow-hidden bg-gray-300 rounded-full shadow-inner">
                          <div 
                            className={`
                              h-full rounded-full shadow-lg transition-all duration-700 hover:shadow-xl
                              relative overflow-hidden
                              ${index === 0 ? 'bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600' :
                                index === 1 ? 'bg-gradient-to-r from-gray-400 via-slate-500 to-gray-600' :
                                index === 2 ? 'bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600' :
                                index === 3 ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-600' :
                                'bg-gradient-to-r from-purple-400 via-indigo-500 to-purple-600'
                              }
                            `}
                            style={{width: `${95 - index * 5}%`}}
                          >
                            {/* Shine Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* 3D Card Corners */}
                      <div className="absolute w-4 h-4 border-t-2 border-l-2 rounded-tl-lg top-2 left-2 border-white/50"></div>
                      <div className="absolute w-4 h-4 border-t-2 border-r-2 rounded-tr-lg top-2 right-2 border-white/50"></div>
                      <div className="absolute w-4 h-4 border-b-2 border-l-2 rounded-bl-lg bottom-2 left-2 border-white/50"></div>
                      <div className="absolute w-4 h-4 border-b-2 border-r-2 rounded-br-lg bottom-2 right-2 border-white/50"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Call-to-Action Section */}
      <div className="p-6 text-center text-white bg-gradient-to-r from-blue-600 via-indigo-700 to-cyan-800 rounded-2xl">
        <div className="max-w-2xl mx-auto space-y-4">
          <h3 className="mb-3 text-2xl font-black">
            🌍 Ready to Dominate Global Wellness?
          </h3>
          
          <p className="font-medium text-blue-100">
            Challenge cities, states, provinces, and countries worldwide! Create epic battles and climb the global rankings.
          </p>
          
          <div className="grid gap-3 text-center md:grid-cols-3">
            <div className="p-3 rounded-lg bg-white/20 backdrop-blur-sm">
              <div className="mb-1 text-xl font-black text-white">6.8M</div>
              <div className="text-xs text-blue-100">Active Warriors</div>
            </div>
            <div className="p-3 rounded-lg bg-white/20 backdrop-blur-sm">
              <div className="mb-1 text-xl font-black text-white">156</div>
              <div className="text-xs text-blue-100">Cities Competing</div>
            </div>
            <div className="p-3 rounded-lg bg-white/20 backdrop-blur-sm">
              <div className="mb-1 text-xl font-black text-white">$2.25M</div>
              <div className="text-xs text-blue-100">Total Prize Pool</div>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            <div className="relative group">
              <div className="absolute transition duration-300 opacity-75 -inset-1 bg-gradient-to-r from-white to-blue-100 rounded-xl group-hover:opacity-100 blur-sm"></div>
              <button className="relative px-8 py-4 text-lg font-black text-blue-700 transition-all duration-300 transform bg-white shadow-xl rounded-xl hover:bg-blue-50 hover:scale-110 hover:shadow-2xl">
                <div className="flex items-center space-x-3">
                  <Users className="w-6 h-6" />
                  <span>🛡️ JOIN YOUR GUILD</span>
                </div>
              </button>
            </div>
            
            <div className="relative group">
              <div className="absolute transition duration-300 opacity-75 -inset-1 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-xl group-hover:opacity-100 blur-sm"></div>
              <button className="relative px-8 py-4 text-lg font-black text-blue-900 transition-all duration-300 transform shadow-xl rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 hover:scale-110 hover:shadow-2xl">
                <div className="flex items-center space-x-3">
                  <Sword className="w-6 h-6" />
                  <span>⚔️ START EPIC BATTLE</span>
                </div>
              </button>
            </div>
            
            <div className="relative group">
              <div className="absolute transition duration-300 opacity-75 -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl group-hover:opacity-100 blur-sm"></div>
              <button className="relative px-8 py-4 text-lg font-black text-white transition-all duration-300 transform shadow-xl rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-110 hover:shadow-2xl">
                <div className="flex items-center space-x-3">
                  <Crown className="w-6 h-6" />
                  <span>👑 CLAIM THRONE</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 COMING SOON IN FUTURE PHASES - Teams & Guilds */}
      <div className="relative p-8 overflow-hidden border-2 border-dashed rounded-3xl border-blue-300/60 bg-gradient-to-br from-blue-50/80 via-cyan-50/60 to-indigo-50/80">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/5 via-cyan-400/5 to-indigo-400/5 animate-pulse"></div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 shadow-xl bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl animate-bounce">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h3 className="mb-3 text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-600 to-indigo-600">
            🚀 Coming Soon in Future Phases
          </h3>
          <p className="max-w-3xl mx-auto mb-4 text-lg font-semibold text-gray-700">
            Advanced guild management systems, team formation algorithms, multi-department tournaments, 
            collaborative wellness challenges, and social gamification features!
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-sm font-bold text-blue-700">
            <span className="px-3 py-1 rounded-full bg-blue-100/80">🛡️ Guild Systems</span>
            <span className="px-3 py-1 rounded-full bg-cyan-100/80">🤝 Team Builder</span>
            <span className="px-3 py-1 rounded-full bg-indigo-100/80">🏅 Multi-Tournaments</span>
            <span className="px-3 py-1 rounded-full bg-blue-100/80">💬 Social Features</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'battle-zone':
        return renderBattleZone();
      case 'teams-guilds':
        return renderTeamsGuilds();
      case 'corporate':
        return renderCorporate();
      case 'competition-central':
        return (
          <div className="relative p-12 overflow-hidden border-2 border-dashed rounded-3xl border-purple-300/60 bg-gradient-to-br from-purple-50/80 via-pink-50/60 to-fuchsia-50/80">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-pink-400/10 to-fuchsia-400/10 animate-pulse"></div>
            <div className="relative z-10 text-center">
              <div className="flex items-center justify-center mb-6">
                <div className="p-6 shadow-2xl bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl animate-bounce">
                  <Crown className="w-12 h-12 text-white" />
                </div>
              </div>
              <h2 className="mb-4 text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-fuchsia-600">
                👑 Competition Central
              </h2>
              <h3 className="mb-6 text-2xl font-bold text-gray-800">
                🚀 Coming Soon in Future Phases
              </h3>
              <p className="max-w-4xl mx-auto mb-8 text-xl font-semibold text-gray-700">
                The ultimate championship arena featuring elite tournaments, world rankings, exclusive events, 
                celebrity wellness challenges, and revolutionary competition formats that will redefine corporate wellness!
              </p>
              <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2 lg:grid-cols-4">
                <div className="p-4 shadow-lg bg-white/80 backdrop-blur-sm rounded-2xl">
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <h4 className="font-black text-purple-700">World Championships</h4>
                  <p className="text-sm text-gray-600">Global elite tournaments</p>
                </div>
                <div className="p-4 shadow-lg bg-white/80 backdrop-blur-sm rounded-2xl">
                  <Star className="w-8 h-8 mx-auto mb-2 text-pink-600" />
                  <h4 className="font-black text-pink-700">Celebrity Challenges</h4>
                  <p className="text-sm text-gray-600">Compete with influencers</p>
                </div>
                <div className="p-4 shadow-lg bg-white/80 backdrop-blur-sm rounded-2xl">
                  <Flame className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                  <h4 className="font-black text-orange-700">Elite Leagues</h4>
                  <p className="text-sm text-gray-600">Exclusive invitation only</p>
                </div>
                <div className="p-4 shadow-lg bg-white/80 backdrop-blur-sm rounded-2xl">
                  <Crown className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                  <h4 className="font-black text-amber-700">Hall of Fame</h4>
                  <p className="text-sm text-gray-600">Legendary achievements</p>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-3 text-sm font-bold">
                <span className="px-4 py-2 text-purple-700 rounded-full bg-purple-100/80">🏆 World Rankings</span>
                <span className="px-4 py-2 text-pink-700 rounded-full bg-pink-100/80">⭐ Celebrity Events</span>
                <span className="px-4 py-2 rounded-full text-fuchsia-700 bg-fuchsia-100/80">👑 Elite Status</span>
                <span className="px-4 py-2 text-purple-700 rounded-full bg-purple-100/80">🌟 Exclusive Access</span>
              </div>
            </div>
          </div>
        );
      default:
        return renderBattleZone();
    }
  };

  return (
    <div className="min-h-screen p-6 mx-auto max-w-7xl bg-gray-50">
      {/* Modern Tab Navigation */}
      <div className="p-2 mb-8 bg-white shadow-lg rounded-2xl">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[200px] relative rounded-xl px-6 py-4 font-bold text-sm transition-all duration-500 group ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-2xl scale-105 z-10`
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-center space-x-3">
                  <IconComponent className={`w-5 h-5 ${
                    activeTab === tab.id ? 'animate-pulse' : 'group-hover:scale-110'
                  } transition-transform duration-300`} />
                  <div className="text-left">
                    <div className="font-black">{tab.name}</div>
                    <div className={`text-xs ${
                      activeTab === tab.id ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {tab.description}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      {renderContent()}

      {/* Battle Details Modal */}
      {selectedBattle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transform animate-slideUp">
            
            {/* Professional Modal Header */}
            <div className={`relative p-8 overflow-hidden text-white rounded-t-3xl bg-gradient-to-br ${
              selectedBattle.urgency === 'LEGENDARY' ? 'from-purple-600 via-purple-700 to-indigo-800' :
              selectedBattle.urgency === 'ULTRA' ? 'from-blue-600 via-blue-700 to-cyan-800' :
              'from-emerald-600 via-emerald-700 to-teal-800'
            }`}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]"></div>
              <div className="relative">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <div className="flex items-center mb-4 space-x-4">
                      <div className={`p-3 rounded-2xl shadow-lg ${
                        selectedBattle.urgency === 'LEGENDARY' ? 'bg-purple-500' :
                        selectedBattle.urgency === 'ULTRA' ? 'bg-blue-500' :
                        'bg-emerald-500'
                      }`}>
                        {selectedBattle.urgency === 'LEGENDARY' ? <Crown className="w-8 h-8 text-white" /> :
                         selectedBattle.urgency === 'ULTRA' ? <Flame className="w-8 h-8 text-white" /> :
                         <Trophy className="w-8 h-8 text-white" />}
                      </div>
                      <div>
                        <h2 className="text-3xl font-black leading-tight">{selectedBattle.name}</h2>
                        <p className="text-lg font-medium text-white/90">{selectedBattle.frequency} • {selectedBattle.duration}</p>
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <Users className="w-5 h-5" />
                        <span className="font-bold">{selectedBattle.participants.toLocaleString()} participants</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5" />
                        <span>{selectedBattle.timeLeft} remaining</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Target className="w-5 h-5" />
                        <span>{selectedBattle.difficulty}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedBattle(null)}
                    className="p-2 transition-all duration-200 text-white/70 hover:text-white hover:bg-white/20 rounded-xl"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 space-y-8">
              
              {/* Future Phase Notice - Most Prominent */}
              <div className="p-6 border-l-4 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-xl font-bold text-blue-900">🚀 Coming in Future Phases</h4>
                    <p className="mb-3 leading-relaxed text-blue-800">
                      This premium wellness challenge will be available in upcoming platform phases. 
                      We're currently focused on step-based challenges while developing these advanced competition features.
                    </p>
                    <div className="flex items-center space-x-2 text-sm text-blue-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-semibold">Join our waitlist to be notified when available!</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Challenge Overview */}
              <div className="space-y-4">
                <h4 className="text-2xl font-bold text-gray-900">Challenge Overview</h4>
                <p className="text-lg leading-relaxed text-gray-600">
                  {selectedBattle.description}
                </p>
                <div className="p-4 border bg-amber-50 border-amber-200 rounded-xl">
                  <p className="font-medium text-amber-800">
                    <span className="font-bold">Exclusive Note:</span> {selectedBattle.exclusiveNote}
                  </p>
                </div>
              </div>

              {/* MediPoints Prize Structure - Visual Display */}
              <div className="space-y-4">
                <h4 className="text-2xl font-bold text-gray-900">🏆 MediPoints Prize Structure</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="p-6 text-center text-white transition-transform transform shadow-lg bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl hover:scale-105">
                    <Crown className="w-10 h-10 mx-auto mb-3" />
                    <div className="mb-1 text-3xl font-black">🥇</div>
                    <div className="mb-1 text-2xl font-bold">{(selectedBattle.rewards.first / 1000).toLocaleString()}K</div>
                    <div className="text-sm opacity-90">MediPoints</div>
                  </div>
                  <div className="p-6 text-center text-white shadow-lg bg-gradient-to-br from-gray-400 to-gray-600 rounded-2xl">
                    <Trophy className="w-10 h-10 mx-auto mb-3" />
                    <div className="mb-1 text-3xl font-black">🥈</div>
                    <div className="mb-1 text-xl font-bold">{(selectedBattle.rewards.second / 1000).toLocaleString()}K</div>
                    <div className="text-sm opacity-90">MediPoints</div>
                  </div>
                  <div className="p-6 text-center text-white shadow-lg bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl">
                    <Star className="w-10 h-10 mx-auto mb-3" />
                    <div className="mb-1 text-3xl font-black">🥉</div>
                    <div className="mb-1 text-xl font-bold">{(selectedBattle.rewards.third / 1000).toLocaleString()}K</div>
                    <div className="text-sm opacity-90">MediPoints</div>
                  </div>
                  <div className="p-6 text-center text-white shadow-lg bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl">
                    <Award className="w-10 h-10 mx-auto mb-3" />
                    <div className="mb-1 text-2xl font-bold">🏅</div>
                    <div className="mb-1 text-lg font-bold">{(selectedBattle.rewards.top10 / 1000).toLocaleString()}K</div>
                    <div className="text-xs opacity-90">4th-10th Place</div>
                  </div>
                </div>
              </div>

              {/* Challenge Details Grid */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                
                {/* Challenge Details */}
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-gray-900">📊 Challenge Details</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <span className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-gray-700">Participants</span>
                      </span>
                      <span className="font-bold text-gray-900">{selectedBattle.participants.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <span className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-gray-700">Duration</span>
                      </span>
                      <span className="font-bold text-gray-900">{selectedBattle.duration}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <span className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-gray-700">Location</span>
                      </span>
                      <span className="font-bold text-gray-900">{selectedBattle.location}</span>
                    </div>
                  </div>
                </div>

                {/* Achievement Badges */}
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-gray-900">🏅 Achievement Badges</h4>
                  <div className="space-y-3">
                    {selectedBattle.badges.map((badge, idx) => (
                      <div key={idx} className="flex items-center p-3 space-x-3 border rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                        <div className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-amber-500">
                          <Trophy className="w-4 h-4" />
                        </div>
                        <span className="font-semibold text-amber-900">{badge}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Special Features */}
              <div className="p-6 border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                <h4 className="mb-4 text-xl font-bold text-green-900">✨ Special Features</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex items-start space-x-3">
                    <Bolt className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-semibold text-green-900">Point Multipliers</div>
                      <div className="text-sm text-green-700">{selectedBattle.multiplier}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Activity className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="font-semibold text-green-900">Real-time Tracking</div>
                      <div className="text-sm text-green-700">Live progress monitoring</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-4 pt-6 border-t sm:flex-row">
                <button className={`flex-1 flex items-center justify-center space-x-2 py-4 px-6 font-bold text-white rounded-xl transition-all duration-300 transform hover:scale-105 ${
                  selectedBattle.urgency === 'LEGENDARY' ? 'bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800' :
                  selectedBattle.urgency === 'ULTRA' ? 'bg-gradient-to-r from-blue-600 to-cyan-700 hover:from-blue-700 hover:to-cyan-800' :
                  'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800'
                }`}>
                  <CheckCircle className="w-5 h-5" />
                  <span>Join Waitlist</span>
                </button>
                <button
                  onClick={() => setSelectedBattle(null)}
                  className="flex items-center justify-center flex-1 px-6 py-4 space-x-2 font-bold text-gray-700 transition-all duration-300 border-2 border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  <X className="w-5 h-5" />
                  <span>Close</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseGamificationHub;
